class CustomProductOptions {
  constructor(productFormId) {
    this.productFormId = productFormId;
    this.dynamicHeaderSelector = new DynamicHeaderSelector();
    this.dynamicLiningSelector = new DynamicLiningSelector();
    this.dynamicHeatSettingSelector = new DynamicHeatSettingSelector();
    this.dynamicTieBacksSelector = new DynamicTieBacksSelector();
    this.dynamicFabricCompatibility = new DynamicFabricCompatibility();
    this.pricePreview = new PricePreviewManager();
    this.dependencyManager = new DependencyManager(); 
    this.formValidator = new ProductFormValidator(this); 
    
    this._lastVariantId = null;
    this._variantChangeTimeout = null;
    this._priceEventsSetup = false;
    this._quantityEventDelegationSetup = false;
    
    window.curtainProductOptions = {
      headerSelector: this.dynamicHeaderSelector,
      liningSelector: this.dynamicLiningSelector,
      heatSettingSelector: this.dynamicHeatSettingSelector,
      tieBacksSelector: this.dynamicTieBacksSelector,
      fabricCompatibility: this.dynamicFabricCompatibility,
      pricePreview: this.pricePreview,
      dependencyManager: this.dependencyManager,
      formValidator: this.formValidator,
      cartManager: null,
      getCurrentAvailableOptions: this.getCurrentAvailableOptions.bind(this)
    };
  }

  async initialize() {
    try {
      await Promise.all([
        this.dynamicHeaderSelector.initialize(),
        this.dynamicLiningSelector.initialize(),
        this.dynamicHeatSettingSelector.initialize(),
        this.dynamicTieBacksSelector.initialize(),
        this.pricePreview.initialize(),
        this.dependencyManager.initialize()
      ]);

      this.initializeAccordions();
      this.initializeProjectNameInput();
      this.setupFabricLiningCompatibility();

      await this.dynamicFabricCompatibility.initialize();

      this.setupPricePreviewEvents();

      this.setupDependencyListeners();

      this.formValidator.initialize();

      if (window.curtainProductOptions) {
        window.curtainProductOptions.cartManager = this.cartManager;
      }

      this.checkAndApplyPresetConfig();

    } catch (error) {
      console.error('Error initializing custom product options:', error);
    }
  }

  initializeAccordions() {
    // Handle both variant and custom option accordions
    const allAccordions = document.querySelectorAll('.custom-product-options-container .product-accordion, .variant-options-container .product-accordion');
    
    allAccordions.forEach((accordion) => {
      this.setupAccordion(accordion);
      this.setupImageSelectors(accordion);
      this.setupSizeInputs(accordion);
      this.setupVariantOptionHandlers(accordion);
    });

    // Auto-open first variant option if available
    const firstVariantAccordion = document.querySelector('.variant-options-container .product-accordion');
    const firstVariantButton = firstVariantAccordion?.querySelector('.acc-title');
    if (firstVariantButton && !firstVariantButton.classList.contains('active')) {
      setTimeout(() => {
        firstVariantButton.click();
      }, 100);
      return;
    }

    // Otherwise, auto-open header option
    const headerAccordion = document.querySelector('.custom-product-options-container .product-accordion');
    const headerButton = headerAccordion?.querySelector('[data-option-group="header"]');
    if (headerButton && !headerButton.classList.contains('active')) {
      setTimeout(() => {
        if (!headerButton.classList.contains('disabled')) {
          headerButton.click();
        }
      }, 200);
    }
  }

  setupAccordion(accordion) {
    const title = accordion.querySelector('.acc-title');
    const content = accordion.querySelector('.acc-content');
    const selectedOptionDisplay = title.querySelector('.selected-option-display');

    function recalculateHeight() {
      if (content.classList.contains('active')) {
        content.style.display = 'block';
        requestAnimationFrame(() => {
          const contentHeight = content.scrollHeight;
          content.style.maxHeight = contentHeight + 'px';
        });
      }
    }

    accordion.recalculateHeight = recalculateHeight;

    title.addEventListener('click', () => {
      if (title.classList.contains('disabled')) {
        return;
      }
      
      const isActive = title.classList.contains('active');

      if (isActive) {
        content.style.maxHeight = '0px';
        title.classList.remove('active');
        content.classList.remove('active');
        content.classList.remove('show-dropdown');
        
        setTimeout(() => {
          if (!content.classList.contains('active')) {
            content.style.display = 'none';
          }
        }, 400);
      } else {
        const accordions = document.querySelectorAll('.custom-product-options-container .product-accordion');
        accordions.forEach((otherAccordion) => {
          if (otherAccordion !== accordion) {
            const otherTitle = otherAccordion.querySelector('.acc-title');
            const otherContent = otherAccordion.querySelector('.acc-content');
            otherTitle.classList.remove('active');
            otherContent.classList.remove('active');
            otherContent.classList.remove('show-dropdown');
            otherContent.style.maxHeight = '0px';
            
            setTimeout(() => {
              if (!otherContent.classList.contains('active')) {
                otherContent.style.display = 'none';
              }
            }, 400);
          }
        });
        
        title.classList.add('active');
        content.classList.add('active');
        content.style.display = 'block';
        
        requestAnimationFrame(() => {
          const contentHeight = content.scrollHeight;
          content.style.maxHeight = contentHeight + 'px';
        });
      }
    });
  }

  setupImageSelectors(accordion) {
    const imageSelectors = accordion.querySelectorAll('.image-selector-container');
    imageSelectors.forEach((container) => {
      const propertyName = container.dataset.propertyName;
      
      if (propertyName === 'Header' || propertyName === 'Lining' || propertyName === 'Heat Setting' || propertyName === 'Tie Backs') {
        return;
      }
      
      const hiddenInput = accordion.querySelector(`input[name="properties[${propertyName}]"]`);
      if (hiddenInput && this.productFormId) {
        hiddenInput.setAttribute('form', this.productFormId);
      }

      const selectedOptionDisplay = accordion.querySelector('.acc-title .selected-option-display');
      const options = container.querySelectorAll('.image-option');
      options.forEach((option) => {
        option.addEventListener('click', () => {
          options.forEach((opt) => opt.classList.remove('selected'));
          option.classList.add('selected');
          if (hiddenInput) {
            hiddenInput.value = option.dataset.value;
          }
          if (selectedOptionDisplay) {
            selectedOptionDisplay.textContent = option.dataset.value;
          }

          if (window.curtainProductOptions?.formValidator) {
            window.curtainProductOptions.formValidator.checkAndClearErrors();
          }
        });
      });
    });
  }

  setupSizeInputs(accordion) {
    const widthInput = accordion.querySelector('input[name="properties[Panel Width (in)]"]');
    const lengthInput = accordion.querySelector('input[name="properties[Panel Length (in)]"]');
    const confirmSizeButton = accordion.querySelector('.confirm-size-button');
    const selectedOptionDisplay = accordion.querySelector('.acc-title .selected-option-display');

    const createOrGetValidationMessage = (inputElement, inputType) => {
      const inputGroup = inputElement.closest('.input-group');
      if (!inputGroup) return null;
      
      let validationMessage = inputGroup.querySelector('.size-validation-message');
      if (!validationMessage) {
        validationMessage = document.createElement('div');
        validationMessage.className = 'size-validation-message';
        validationMessage.dataset.inputType = inputType; 
        
        inputGroup.appendChild(validationMessage);
      }
      return validationMessage;
    };

    const validateSingleInput = (inputElement, inputType) => {
      if (!inputElement) return true;
      
      const value = parseFloat(inputElement.value);
      const validationMessage = createOrGetValidationMessage(inputElement, inputType);
      
      if (!validationMessage) return true;
      
      let showError = false;
      let errorMessage = '';
      
      if (!isNaN(value) && value > 0 && value < 12) {
        showError = true;
        errorMessage = inputType === 'width' ? 'Minimum width is 12 inches' : 'Minimum length is 12 inches';
      }
      
      if (showError) {
        validationMessage.textContent = errorMessage;
        validationMessage.style.display = 'block';
        
        inputElement.style.borderColor = '#d32f2f';
      } else {
        validationMessage.style.display = 'none';
        
        inputElement.style.borderColor = '';
      }
      
      return isNaN(value) || value === 0 || value >= 12;
    };

    const validateSizeInputs = () => {
      const widthValid = validateSingleInput(widthInput, 'width');
      const lengthValid = validateSingleInput(lengthInput, 'length');
      
      if (accordion && accordion.recalculateHeight) {
        requestAnimationFrame(() => {
          accordion.recalculateHeight();
        });
      }
      
      return widthValid && lengthValid;
    };

    function updateSizeDisplay() {
      if (widthInput && lengthInput && selectedOptionDisplay) {
        const widthVal = widthInput.value;
        const lengthVal = lengthInput.value;
        if (widthVal && lengthVal) {
          selectedOptionDisplay.textContent = `${widthVal}"W x ${lengthVal}"L`;
        } else if (widthVal) {
          selectedOptionDisplay.textContent = `${widthVal}"W`;
        } else if (lengthVal) {
          selectedOptionDisplay.textContent = `${lengthVal}"L`;
        } else {
          selectedOptionDisplay.textContent = '';
        }
      }
      
      validateSizeInputs();
    }

    if (widthInput && this.productFormId) widthInput.setAttribute('form', this.productFormId);
    if (lengthInput && this.productFormId) lengthInput.setAttribute('form', this.productFormId);
    const projectNameInput = accordion.querySelector('input[name="properties[Project Name]"]');
    if (projectNameInput && this.productFormId) projectNameInput.setAttribute('form', this.productFormId);

    if (widthInput) {
      widthInput.addEventListener('input', () => {
        updateSizeDisplay();
        validateSingleInput(widthInput, 'width');
        // Update heat setting price display when dimensions change
        if (window.curtainProductOptions?.heatSettingSelector) {
          window.curtainProductOptions.heatSettingSelector.updateHeatSettingPriceDisplay();
        }
        // Update lining price display when dimensions change
        if (window.curtainProductOptions?.liningSelector) {
          window.curtainProductOptions.liningSelector.updateLiningPriceDisplay();
        }
      });
      widthInput.addEventListener('blur', () => {
        validateSingleInput(widthInput, 'width');
      });
    }
    if (lengthInput) {
      lengthInput.addEventListener('input', () => {
        updateSizeDisplay();
        validateSingleInput(lengthInput, 'length');
        // Update heat setting price display when dimensions change
        if (window.curtainProductOptions?.heatSettingSelector) {
          window.curtainProductOptions.heatSettingSelector.updateHeatSettingPriceDisplay();
        }
        // Update lining price display when dimensions change
        if (window.curtainProductOptions?.liningSelector) {
          window.curtainProductOptions.liningSelector.updateLiningPriceDisplay();
        }
      });
      lengthInput.addEventListener('blur', () => {
        validateSingleInput(lengthInput, 'length');
      });
    }

    if (confirmSizeButton) {
      confirmSizeButton.addEventListener('click', () => {
        updateSizeDisplay();
        validateSizeInputs();
      });
    }
  }



  setupVariantOptionHandlers(accordion) {
    // Check if this is a variant accordion
    const variantOptionBtn = accordion.querySelector('[data-variant-option]');
    if (!variantOptionBtn) return;

    const selectedOptionDisplay = accordion.querySelector('.selected-option-display');
    const variantSelects = accordion.querySelector('variant-selects');
    
    if (!variantSelects || !selectedOptionDisplay) return;

    // Listen for variant changes to update the display
    const variantInputs = accordion.querySelectorAll('input[type="radio"]');
    
    variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) {
          // Get the label text or value
          const label = accordion.querySelector(`label[for="${input.id}"]`);
          const displayValue = label ? label.textContent.trim() : input.value;
          selectedOptionDisplay.textContent = displayValue;
        }
      });
    });

    // Set initial display value
    const checkedInput = accordion.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      const label = accordion.querySelector(`label[for="${checkedInput.id}"]`);
      const displayValue = label ? label.textContent.trim() : checkedInput.value;
      selectedOptionDisplay.textContent = displayValue;
    }
  }



  setupFabricLiningCompatibility() {
    document.addEventListener('fabricChanged', (event) => {
      if (this.dynamicLiningSelector && this.dynamicLiningSelector.isInitialized) {
        this.dynamicLiningSelector.updateLiningOptionsForFabric(event.detail.fabricId);
      }
    });
  }

  initializeProjectNameInput() {
    const projectInputContainer = document.querySelector('.custom-product-options-container .project-name-input-container');
    if (projectInputContainer) {
      const projectNameInput = projectInputContainer.querySelector('#project-name');
      
      if (projectNameInput && this.productFormId) {
        projectNameInput.setAttribute('form', this.productFormId);
      }
    }
  }

  setupPricePreviewEvents() {
    if (this._priceEventsSetup) {
      return;
    }
    this._priceEventsSetup = true;

    document.addEventListener('configurationChanged', () => {
      
      this.pricePreview.updatePreview();
    });

    document.addEventListener('fabricChanged', () => {
      this.pricePreview.updatePreview();
    });

    document.addEventListener('productPricingChanged', () => {
      this.pricePreview.updatePreview();
    });

    const container = document.querySelector('.custom-product-options-container');
    if (container) {
      container.addEventListener('input', this.debounce(() => {
        this.pricePreview.updatePreview();
      }, 300));

      container.addEventListener('change', () => {
        this.pricePreview.updatePreview();
      });
    }

    // 使用事件委托来处理 quantity input 事件，避免 variant 切换后事件失效
    this.setupQuantityEventDelegation();

    if (this.productFormId) {
      const productForm = document.getElementById(this.productFormId);
      if (productForm) {
        const variantSelectors = productForm.querySelectorAll('select[name="id"], input[name="id"]:not([type="radio"])');
        variantSelectors.forEach(selector => {
          if (!selector.dataset.priceEventsBound) {
            selector.dataset.priceEventsBound = 'true';
            selector.addEventListener('change', this.debounce((event) => {
              this.handleVariantChange(event.target.value);
              // variant 切换后重新绑定 quantity 事件
              setTimeout(() => this.rebindQuantityEvents(), 100);
            }, 100));
          }
        });

        const variantRadios = productForm.querySelectorAll('input[type="radio"][name="id"], input[type="radio"][data-variant-id]');
        variantRadios.forEach(radio => {
          if (!radio.dataset.priceEventsBound) {
            radio.dataset.priceEventsBound = 'true';
            radio.addEventListener('change', this.debounce((event) => {
              if (event.target.checked) {
                const variantId = event.target.value || event.target.getAttribute('data-variant-id');
                if (variantId) {
                  this.handleVariantChange(variantId);
                  // variant 切换后重新绑定 quantity 事件
                  setTimeout(() => this.rebindQuantityEvents(), 100);
                }
              }
            }, 100));
          }
        });
      }
    }

    // 监听动态添加的 variant 选择器
    document.addEventListener('change', this.debounce((event) => {
      if (event.target.matches('input[name="id"], select[name="id"], input[type="radio"][data-variant-id]') &&
          !event.target.dataset.priceEventsBound) {
        const variantId = event.target.value || event.target.getAttribute('data-variant-id');
        if (variantId) {
          this.handleVariantChange(variantId);
          // variant 切换后重新绑定 quantity 事件
          setTimeout(() => this.rebindQuantityEvents(), 100);
        }
      }
    }, 100));

    document.addEventListener('product:variant-change', this.debounce((event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantChange(event.detail.variant.id);
        // variant 切换后重新绑定 quantity 事件
        setTimeout(() => this.rebindQuantityEvents(), 100);
      }
    }, 100));
  }

  handleVariantChange(variantId) {
    try {
      if (this._lastVariantId === variantId) {
        return;
      }
      this._lastVariantId = variantId;

      if (this._variantChangeTimeout) {
        clearTimeout(this._variantChangeTimeout);
      }

      this._variantChangeTimeout = setTimeout(() => {
        this._performVariantChange(variantId);
      }, 50);
    } catch (error) {
      console.error('Error handling variant change:', error);
    }
  }

  _performVariantChange(variantId) {
    try {
      if (window.product && window.product.variants) {
        const selectedVariant = window.product.variants.find(variant => 
          variant.id.toString() === variantId.toString()
        );
        
        if (selectedVariant) {
          const previousPricing = window.currentProductPricing ? { ...window.currentProductPricing } : {};
          window.currentProductPricing = {
            ...previousPricing,
            basePrice: selectedVariant.price,
            basePriceDecimal: selectedVariant.price / 100, 
            variantId: selectedVariant.id,
            variantTitle: selectedVariant.title,
            updated: Date.now()
          };

          const priceChangeEvent = new CustomEvent('productPricingChanged', {
            detail: {
              variantId: selectedVariant.id,
              price: selectedVariant.price,
              priceDecimal: selectedVariant.price / 100,
              variant: selectedVariant,
              previousPricing: previousPricing
            }
          });
          document.dispatchEvent(priceChangeEvent);

          if (this.pricePreview && this.pricePreview.isInitialized) {
            setTimeout(() => {
              this.pricePreview.updatePreview();
            }, 100);
          }
        }
      }
    } catch (error) {
      console.error('Error performing variant change:', error);
    }
  }

  setupQuantityEventDelegation() {
    // 使用事件委托来处理 quantity input 的变化，避免 variant 切换后事件失效
    // 确保只绑定一次事件监听器
    if (!this._quantityEventDelegationSetup) {
      this._quantityEventDelegationSetup = true;
      
      document.addEventListener('change', (event) => {
        if (event.target.matches('input[name="quantity"]') &&
            event.target.closest(`#${this.productFormId}`)) {
          this.pricePreview.updatePreview();
          this.updateTieBacksOnQuantityChange();
        }
      });

      document.addEventListener('input', this.debounce((event) => {
        if (event.target.matches('input[name="quantity"]') &&
            event.target.closest(`#${this.productFormId}`)) {
          this.pricePreview.updatePreview();
        }
      }, 300));
    }
  }



  rebindQuantityEvents() {
    // 重新绑定 quantity 相关的事件，确保在 variant 切换后仍然有效
    if (this.productFormId) {
      const productForm = document.getElementById(this.productFormId);
      if (productForm) {
        // 先清理之前的标记
        const oldQuantityInputs = productForm.querySelectorAll('input[name="quantity"][data-events-rebound]');
        oldQuantityInputs.forEach(input => {
          input.removeAttribute('data-events-rebound');
        });
        
        // 重新绑定当前的 quantity input
        const quantityInput = productForm.querySelector('input[name="quantity"]');
        if (quantityInput && !quantityInput.dataset.eventsRebound) {
          quantityInput.dataset.eventsRebound = 'true';
          
          // 确保价格预览更新
          quantityInput.addEventListener('change', () => {
            this.pricePreview.updatePreview();
            this.updateTieBacksOnQuantityChange();
          });
          
          quantityInput.addEventListener('input', this.debounce(() => {
            this.pricePreview.updatePreview();
          }, 300));
        }
      }
    }
  }

  updateTieBacksOnQuantityChange() {
    // 更新 tie backs 相关计算
    const tieBacksContainer = document.querySelector('.image-selector-container[data-property-name="Tie Backs"]');
    if (tieBacksContainer) {
      const selectedOption = tieBacksContainer.querySelector('.image-option.selected');
      if (selectedOption && window.curtainProductOptions?.tieBacksSelector) {
        // 触发 tie backs 重新计算
        window.curtainProductOptions.tieBacksSelector.updateCalculation(selectedOption);
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setupDependencyListeners() {
    document.addEventListener('configurationChanged', (event) => {
      this.dependencyManager.updateDependencies();
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="properties[Header]"]')) {
        this.dependencyManager.updateDependencies();
      }
    });

    const sizeInputs = document.querySelectorAll('input[name="properties[Panel Width (in)]"], input[name="properties[Panel Length (in)]"]');
    sizeInputs.forEach(input => {
      input.addEventListener('input', this.debounce(() => {
        this.dependencyManager.updateDependencies();
      }, 500));
    });
  }

  checkAndApplyPresetConfig() {
    try {
      
      // First try to get pod configuration
      const podConfig = this.getPodConfiguration();
      if (podConfig) {
        this.applyPresetConfig(podConfig);
        return;
      }

      const STORAGE_KEY = 'curtain_preset_config';
      const presetData = localStorage.getItem(STORAGE_KEY);
      
      if (!presetData) {
        return;
      }

      const config = JSON.parse(presetData);
      
      if (config.expiresAt && config.expiresAt > 0 && Date.now() > config.expiresAt) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      if (config.productId && window.product && window.product.id.toString() !== config.productId.toString()) {
        return;
      }

      this.applyPresetConfig(config);
      
    } catch (error) {
      console.error('❌ Error applying preset configuration:', error);
      localStorage.removeItem('curtain_preset_config');
    }
  }

  getPodConfiguration() {
    try {
      // Use the shared StorageUtil to get pod configuration
      const podStorageUtil = new StorageUtil('pod_');
      const podData = podStorageUtil.getPodConfig('config');

      if (!podData) {
        return null;
      }


      // Map pod fields to curtain configuration format
      return this.mapPodDataToCurtainConfig(podData);

    } catch (error) {
      console.error('Error getting pod configuration:', error);
      return null;
    }
  }



  mapPodDataToCurtainConfig(podData) {
    const config = {};

    // Map header field - pod uses 'header' key
    if (podData.header) {
      const headerValue = podData.header;
      config.header = this.mapPodHeaderToCurtainHeader(headerValue);
    }

    // Map size fields - pod uses 'width' and 'height'
    if (podData.width) {
      const width = podData.width;
      // Remove any non-numeric characters and convert to number
      const numericWidth = parseFloat(width.toString().replace(/[^0-9.]/g, ''));
      if (numericWidth && numericWidth > 0) {
        if (!config.size) config.size = {};
        config.size.width = numericWidth;
      }
    }

    if (podData.height) {
      const height = podData.height;
      // Remove any non-numeric characters and convert to number
      const numericHeight = parseFloat(height.toString().replace(/[^0-9.]/g, ''));
      if (numericHeight && numericHeight > 0) {
        if (!config.size) config.size = {};
        config.size.length = numericHeight; // Map height to length for curtain
      }
    }

    // Map panel field - this could affect lining selection
    if (podData.panel) {
      config.panel = podData.panel;
      // If panel is "Split Panel", we might want to suggest certain lining options
      if (podData.panel.includes('Split')) {
        // Could set a specific lining preference here
      }
    }

    // Map fullness field - this affects the fabric calculation
    if (podData.fullness) {
      config.fullness = podData.fullness;
    }

    // Map finished length field - this affects how the curtain hangs
    if (podData.finished_length) {
      config.finishedLength = podData.finished_length;
      
      // Based on finished length, we might suggest different settings
      if (podData.finished_length.includes('Kiss Floor')) {
        // Standard length, no special adjustments needed
      } else if (podData.finished_length.includes('Puddle')) {
        // Longer length, might suggest different header types
      }
    }

    // Set intelligent defaults based on pod selections
    
    // Default lining based on panel type
    if (!config.lining) {
      // if (podData.panel && podData.panel.includes('Blackout')) {
      //   config.lining = {
      //     id: 'blackout_lining',
      //     value: 'Blackout Lining'
      //   };
      // } else if (podData.panel && podData.panel.includes('Room Darking')) {
      //   config.lining = {
      //     id: 'room_darking_lining',
      //     value: 'Room Darking Lining'
      //   };
      // } else {
      //   config.lining = {
      //     id: 'unlined',
      //     value: 'Unlined'
      //   };
      // }
    }

    // Default heat setting - recommend for certain fabric types or fullness
    if (!config.heatSetting) {
      // If high fullness, recommend heat setting for better draping
      // if (podData.fullness && (podData.fullness.includes('2.5x') || podData.fullness.includes('3.0x'))) {
      //   config.heatSetting = 'yes';
      // } else {
      //   config.heatSetting = 'no';
      // }
    }

    // Default tie backs
    if (!config.tieBacks) {
      config.tieBacks = 'none';
    }

    // Add project name if we can infer it from pod data
    if (podData.projectName) {
      config.projectName = podData.projectName;
    }

    // Set default quantity
    if (!config.quantity) {
      config.quantity = 1;
    }

    return config;
  }

  mapPodHeaderToCurtainHeader(podHeaderValue) {
    // Create mapping from pod header names to curtain header configuration
    const headerMappings = {
      // Pod header names to curtain header names (exact matches)
      'Grommet': { id: 'grommet', value: 'Grommet' },
      'Goblet Pleat': { id: 'goblet_pleat', value: 'Goblet Pleat' },
      'Triple Pleat': { id: 'triple_pleat', value: 'Triple Pleat' },
      'Pinch Pleat': { id: 'pinch_pleat', value: 'Pinch Pleat' },
      'Inverted Box': { id: 'inverted_box', value: 'Inverted Box' },
      'Back Tab & Rod Pocket': { id: 'back_tab_rod_pocket', value: 'Back Tab & Rod Pocket' },
      
      // Pod specific formats with hyphens (like "Pinch Pleat-Triple")
      'Pinch Pleat-Triple': { id: 'triple_pleat', value: 'Triple Pleat' },
      'Pinch Pleat-Double': { id: 'pinch_pleat', value: 'Pinch Pleat' },
      'Goblet Pleat-Standard': { id: 'goblet_pleat', value: 'Goblet Pleat' },
      'Grommet-Standard': { id: 'grommet', value: 'Grommet' },
      'Inverted Box-Standard': { id: 'inverted_box', value: 'Inverted Box' },
      
      // Alternative names that might be used in pod (lowercase)
      'grommet': { id: 'grommet', value: 'Grommet' },
      'goblet pleat': { id: 'goblet_pleat', value: 'Goblet Pleat' },
      'goblet_pleat': { id: 'goblet_pleat', value: 'Goblet Pleat' },
      'triple pleat': { id: 'triple_pleat', value: 'Triple Pleat' },
      'triple_pleat': { id: 'triple_pleat', value: 'Triple Pleat' },
      'pinch pleat': { id: 'pinch_pleat', value: 'Pinch Pleat' },
      'pinch_pleat': { id: 'pinch_pleat', value: 'Pinch Pleat' },
      'inverted box': { id: 'inverted_box', value: 'Inverted Box' },
      'inverted_box': { id: 'inverted_box', value: 'Inverted Box' },
      'back tab & rod pocket': { id: 'back_tab_rod_pocket', value: 'Back Tab & Rod Pocket' },
      'back_tab_rod_pocket': { id: 'back_tab_rod_pocket', value: 'Back Tab & Rod Pocket' },
      'rod pocket': { id: 'back_tab_rod_pocket', value: 'Back Tab & Rod Pocket' },
      'rod_pocket': { id: 'back_tab_rod_pocket', value: 'Back Tab & Rod Pocket' },
      
      // Image key mappings (based on pod.js image logic)
      'ripple_fold': { id: 'grommet', value: 'Grommet' },
      'default': { id: 'grommet', value: 'Grommet' },
      
      // Additional variations that might be used
      'eyelet': { id: 'grommet', value: 'Grommet' },
      'ring top': { id: 'grommet', value: 'Grommet' },
      'tab top': { id: 'back_tab_rod_pocket', value: 'Back Tab & Rod Pocket' },
      'pencil pleat': { id: 'pinch_pleat', value: 'Pinch Pleat' },
      'french pleat': { id: 'triple_pleat', value: 'Triple Pleat' },
    };

    if (!podHeaderValue) {
      console.warn('No pod header value provided, using default grommet');
      return { id: 'grommet', value: 'Grommet' };
    }

    // Convert to string and trim
    const headerStr = podHeaderValue.toString().trim();

    // Try exact match first
    if (headerMappings[headerStr]) {
      return headerMappings[headerStr];
    }

    // Try case-insensitive match
    const lowerValue = headerStr.toLowerCase();
    if (headerMappings[lowerValue]) {
      return headerMappings[lowerValue];
    }

    // Try partial match (contains)
    for (const [key, mapping] of Object.entries(headerMappings)) {
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes(lowerValue) || lowerValue.includes(lowerKey)) {
        return mapping;
      }
    }

    // Try intelligent parsing for compound names (e.g., "Pinch Pleat-Triple")
    if (lowerValue.includes('-')) {
      const [mainType, subType] = lowerValue.split('-');
      const mainTypeTrimmed = mainType.trim();
      
      // Handle specific sub-type mappings
      if (subType && subType.trim().toLowerCase() === 'triple') {
        if (mainTypeTrimmed.toLowerCase().includes('pleat')) {
          return { id: 'triple_pleat', value: 'Triple Pleat' };
        }
      }
      
      // Try matching just the main type
      for (const [key, mapping] of Object.entries(headerMappings)) {
        if (key.toLowerCase().includes(mainTypeTrimmed.toLowerCase()) || 
            mainTypeTrimmed.toLowerCase().includes(key.toLowerCase())) {
          return mapping;
        }
      }
    }

    // Try word-based matching for compound names
    const headerWords = lowerValue.split(/[\s_-]+/);
    for (const [key, mapping] of Object.entries(headerMappings)) {
      const keyWords = key.toLowerCase().split(/[\s_-]+/);
      const hasCommonWords = headerWords.some(word => 
        keyWords.some(keyWord => word.includes(keyWord) || keyWord.includes(word))
      );
      if (hasCommonWords) {
        return mapping;
      }
    }

    // Default fallback
    console.warn('Could not map pod header value:', podHeaderValue, 'using default grommet');
    return { id: 'grommet', value: 'Grommet' };
  }

  async applyPresetConfig(config) {
    await new Promise(resolve => setTimeout(resolve, 800));


    if (config.header) {
      await this.applyHeaderPreset(config.header);
    }

    if (config.size) {
      this.applySizePreset(config.size);
    }

    if (config.lining) {
      await this.applyLiningPreset(config.lining);
    }

    if (config.heatSetting) {
      this.applyHeatSettingPreset(config.heatSetting);
    }

    if (config.tieBacks) {
      this.applyTieBacksPreset(config.tieBacks);
    }

    if (config.projectName) {
      this.applyProjectNamePreset(config.projectName);
    }

    if (config.quantity) {
      this.applyQuantityPreset(config.quantity);
    }

    setTimeout(() => {
      if (this.pricePreview && this.pricePreview.updatePreview) {
        this.pricePreview.updatePreview();
      }
    }, 500);

  }

  async applyHeaderPreset(headerConfig) {
    let attempts = 0;
    while (attempts < 15) {
      const headerOptions = document.querySelectorAll('.image-selector-container[data-property-name="Header"] .image-option');
      if (headerOptions.length > 0) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      attempts++;
    }

    const availableOptions = document.querySelectorAll('.image-selector-container[data-property-name="Header"] .image-option');

    let headerOption = document.querySelector(
      `.image-selector-container[data-property-name="Header"] .image-option[data-value="${headerConfig.value}"]`
    );
    
    if (!headerOption && headerConfig.id) {
      headerOption = document.querySelector(
        `.image-selector-container[data-property-name="Header"] .image-option[data-header-id="${headerConfig.id}"]`
      );
    }

    if (!headerOption) {
      const searchValue = headerConfig.value.toLowerCase();
      headerOption = Array.from(availableOptions).find(opt => 
        opt.dataset.value.toLowerCase().includes(searchValue) ||
        searchValue.includes(opt.dataset.value.toLowerCase())
      );
    }
    
    if (headerOption) {
      headerOption.click();
      const headerButton = document.querySelector('[data-option-group="header"]');
      if (headerButton && !headerButton.classList.contains('active')) {
        headerButton.click();
      }
    } else {
      console.warn('❌ header option not found:', headerConfig.value);
    }
  }

  applySizePreset(sizeConfig) {
    const widthInput = document.querySelector('input[name="properties[Panel Width (in)]"]');
    const lengthInput = document.querySelector('input[name="properties[Panel Length (in)]"]');
    
    if (widthInput && sizeConfig.width) {
      widthInput.value = sizeConfig.width;
      widthInput.dispatchEvent(new Event('input', { bubbles: true }));
      widthInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    if (lengthInput && sizeConfig.length) {
      lengthInput.value = sizeConfig.length;
      lengthInput.dispatchEvent(new Event('input', { bubbles: true }));
      lengthInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
  }

  async applyLiningPreset(liningConfig) {
    let attempts = 0;
    while (attempts < 15) {
      const liningOptions = document.querySelectorAll('.image-selector-container[data-property-name="Lining"] .image-option');
      if (liningOptions.length > 0) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      attempts++;
    }

    const availableOptions = document.querySelectorAll('.image-selector-container[data-property-name="Lining"] .image-option');

    let liningOption = document.querySelector(
      `.image-selector-container[data-property-name="Lining"] .image-option[data-lining-id="${liningConfig.id}"]`
    );
    
    if (!liningOption && liningConfig.value) {
      liningOption = document.querySelector(
        `.image-selector-container[data-property-name="Lining"] .image-option[data-value="${liningConfig.value}"]`
      );
    }

    if (!liningOption) {
      const searchValue = liningConfig.value ? liningConfig.value.toLowerCase() : liningConfig.id.toLowerCase();
      liningOption = Array.from(availableOptions).find(opt => 
        opt.dataset.value.toLowerCase().includes(searchValue) ||
        opt.dataset.liningId.toLowerCase().includes(searchValue) ||
        searchValue.includes(opt.dataset.value.toLowerCase())
      );
      if (liningOption) {
      }
    }
    
    if (liningOption) {
      liningOption.click();
    }
  }

  applyHeatSettingPreset(heatSetting) {
    const heatOption = document.querySelector(`.image-selector-container[data-property-name="Heat Setting"] .image-option[data-value="${heatSetting}"]`);
    if (heatOption) {
      heatOption.click();
    }
  }

  applyTieBacksPreset(tieBacks) {
    const tieOption = document.querySelector(`.image-selector-container[data-property-name="Tie Backs"] .image-option[data-value="${tieBacks}"]`);
    if (tieOption) {
      tieOption.click();
    }
  }

  applyProjectNamePreset(projectName) {
    const projectInput = document.querySelector('input[name="properties[Project Name]"]');
    if (projectInput) {
      projectInput.value = projectName;
      projectInput.dispatchEvent(new Event('input', { bubbles: true }));
      projectInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  applyQuantityPreset(quantity) {
    const productForm = document.getElementById(this.productFormId);
    if (productForm) {
      const quantityInput = productForm.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }


  getCurrentAvailableOptions() {
    const result = {
      headers: [],
      linings: [],
      heatSettings: [],
      tieBacks: []
    };

    const headerOptions = document.querySelectorAll('.image-selector-container[data-property-name="Header"] .image-option');
    result.headers = Array.from(headerOptions).map(opt => ({
      value: opt.dataset.value,
      id: opt.dataset.headerId
    }));

    const liningOptions = document.querySelectorAll('.image-selector-container[data-property-name="Lining"] .image-option');
    result.linings = Array.from(liningOptions).map(opt => ({
      value: opt.dataset.value,
      id: opt.dataset.liningId
    }));

    const heatOptions = document.querySelectorAll('.image-selector-container[data-property-name="Heat Setting"] .image-option');
    result.heatSettings = Array.from(heatOptions).map(opt => ({
      value: opt.dataset.value,
      enabled: !opt.classList.contains('disabled')
    }));

    const tieOptions = document.querySelectorAll('.image-selector-container[data-property-name="Tie Backs"] .image-option');
    result.tieBacks = Array.from(tieOptions).map(opt => ({
      value: opt.dataset.value,
      enabled: !opt.classList.contains('disabled')
    }));

    return result;
  }

  // 清理方法，用于清理事件监听器
  cleanup() {
    if (this._variantChangeTimeout) {
      clearTimeout(this._variantChangeTimeout);
      this._variantChangeTimeout = null;
    }
    
    // 重置标志
    this._priceEventsSetup = false;
    this._quantityEventDelegationSetup = false;
  }
}

class DynamicHeaderSelector {
  constructor() {
    this.configLoader = null;
    this.isInitialized = false;
  }

  async initialize() {
    try {
      this.configLoader = await window.curtainConfigLoader.getInitializationPromise();
      
      this.generateHeaderOptions();
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing dynamic header selector:', error);
      this.showErrorState();
    }
  }

  generateHeaderOptions() {
    const headerButton = document.querySelector('[data-option-group="header"]');
    if (!headerButton) {
      console.error('Header button not found');
      return;
    }
    
    const headerAccordion = headerButton.closest('.product-accordion');
    if (!headerAccordion) {
      console.error('Header accordion not found');
      return;
    }
    
    const headerContainer = headerAccordion.querySelector('.image-selector-container');
    const loadingElement = headerAccordion.querySelector('.header-loading');

    
    if (!headerContainer || !this.configLoader) {
      console.error('Header container or config loader not found');
      return;
    }

    try {
      const headerTypes = this.configLoader.getEnabledHeaderTypes();
      
      headerContainer.innerHTML = '';
      
      Object.values(headerTypes).forEach(header => {
        const optionElement = this.createHeaderOption(header);
        headerContainer.appendChild(optionElement);
      });

      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
      headerContainer.style.display = 'flex';

      this.setupHeaderOptionHandlers();

      // Check if a header is already selected and show price if needed
      this.checkInitialHeaderSelection();

      if (headerAccordion && headerAccordion.recalculateHeight) {
        headerAccordion.recalculateHeight();
      }
      
    } catch (error) {
      console.error('Error generating header options:', error);
      this.showErrorState();
    }
  }

  createHeaderOption(header) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'image-option';
    button.dataset.value = header.name;
    button.dataset.headerId = header.id;
    button.dataset.fee = header.fixed_fee;

    button.innerHTML = `
      <img
        src="${header.image}"
        alt="${header.name}"
        width="100"
        height="100"
      >
      <div class="option-details">
        <div class="option-name">${header.name}</div>
      </div>
    `;

    return button;
  }

  setupHeaderOptionHandlers() {
    const headerButton = document.querySelector('[data-option-group="header"]');
    if (!headerButton) {
      console.error('Header button not found in setupHeaderOptionHandlers');
      return;
    }
    
    const headerAccordion = headerButton.closest('.product-accordion');
    if (!headerAccordion) {
      console.error('Header accordion not found in setupHeaderOptionHandlers');
      return;
    }
    
    const headerContainer = headerAccordion.querySelector('.image-selector-container');
    const hiddenInput = headerAccordion.querySelector('input[name="properties[Header]"]');
    const headerIdInput = headerAccordion.querySelector('input[name="properties[_Header ID]"]');
    const feeInput = headerAccordion.querySelector('input[name="properties[_Header Fee]"]');
    const selectedOptionDisplay = headerButton.querySelector('.selected-option-display');

    const container = document.querySelector('.custom-product-options-container');
    const productFormId = container?.dataset.productFormId || '';
    if (hiddenInput && productFormId) {
      hiddenInput.setAttribute('form', productFormId);
    }
    if (headerIdInput && productFormId) {
      headerIdInput.setAttribute('form', productFormId);
    }
    if (feeInput && productFormId) {
      feeInput.setAttribute('form', productFormId);
    }

    if (!headerContainer) return;

    const options = headerContainer.querySelectorAll('.image-option');
    options.forEach((option) => {
      option.addEventListener('click', () => {
        
        options.forEach((opt) => opt.classList.remove('selected'));
        
        option.classList.add('selected');
        
        if (hiddenInput) {
          hiddenInput.value = option.dataset.value;
        }
        if (headerIdInput) {
          headerIdInput.value = option.dataset.headerId;
        }
        if (feeInput) {
          feeInput.value = option.dataset.fee;
        }
        
        if (selectedOptionDisplay) {
          selectedOptionDisplay.textContent = option.dataset.value;
        }
        
        const configChangeEvent = new CustomEvent('configurationChanged', {
          detail: { type: 'header', value: option.dataset.value }
        });
        document.dispatchEvent(configChangeEvent);

        // Show price after header selection
        this.showPriceAfterHeaderSelection();

        // Update heat setting price display when header changes
        if (window.curtainProductOptions?.heatSettingSelector) {
          setTimeout(() => {
            window.curtainProductOptions.heatSettingSelector.updateHeatSettingPriceDisplay();
          }, 100);
        }

        // Update lining price display when header changes
        if (window.curtainProductOptions?.liningSelector) {
          setTimeout(() => {
            window.curtainProductOptions.liningSelector.updateLiningPriceDisplay();
          }, 100);
        }
        
      });
    });
  }

  showErrorState() {
    const headerButton = document.querySelector('[data-option-group="header"]');
    if (!headerButton) {
      console.error('Header button not found in showErrorState');
      return;
    }

    const headerAccordion = headerButton.closest('.product-accordion');
    if (!headerAccordion) {
      console.error('Header accordion not found in showErrorState');
      return;
    }

    const loadingElement = headerAccordion.querySelector('.header-loading');
    if (loadingElement) {
      loadingElement.innerHTML = '<p style="color: #d32f2f;">Error loading header options. Please refresh the page.</p>';
    }
  }

  checkInitialHeaderSelection() {
    // Check if a header is already selected on page load
    const headerInput = document.querySelector('input[name="properties[Header]"]');
    if (headerInput && headerInput.value && headerInput.value.trim() !== '') {
      // Header is already selected, show price
      this.showPriceAfterHeaderSelection();
    }
  }

  showPriceAfterHeaderSelection() {
    // Show all price elements that were hidden until header selection
    const priceElements = document.querySelectorAll('.price-hidden-until-header-selected');
    priceElements.forEach(priceElement => {
      priceElement.style.display = '';
      priceElement.classList.add('price-revealed');
    });

    // Dispatch event to notify other components that price is now visible
    const priceRevealedEvent = new CustomEvent('priceRevealed', {
      detail: { trigger: 'header-selection' }
    });
    document.dispatchEvent(priceRevealedEvent);

    console.log('Price revealed after header selection');
  }
}

// Dynamic Lining Selector Generator with Fabric Compatibility
class DynamicLiningSelector {
  constructor() {
    this.configLoader = null;
    this.isInitialized = false;
    this.currentFabricId = null;
  }

  async initialize() {
    try {
      // Use centralized configuration initialization
      this.configLoader = await window.curtainConfigLoader.getInitializationPromise();
      
      this.generateLiningOptions();
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing dynamic lining selector:', error);
      this.showErrorState();
    }
  }

  generateLiningOptions() {
console.log('Generating lining options...');
    const liningButton = document.querySelector('[data-option-group="lining"]');
    if (!liningButton) {
      console.error('Lining button not found');
      return;
    }
    
    const liningAccordion = liningButton.closest('.product-accordion');
    if (!liningAccordion) {
      console.error('Lining accordion not found');
      return;
    }
    
    const liningContainer = liningAccordion.querySelector('.image-selector-container');
    const loadingElement = liningAccordion.querySelector('.lining-loading');
    
    if (!liningContainer || !this.configLoader) {
      console.error('Lining container or config loader not found');
      return;
    }

    try {
      // Get enabled lining options from configuration
      const liningOptions = this.configLoader.getEnabledLiningOptions();
      
      // Clear existing content
      liningContainer.innerHTML = '';
      
      // Generate lining options (initially show all, will filter based on fabric)
      Object.values(liningOptions).forEach(lining => {
        const optionElement = this.createLiningOption(lining);
        liningContainer.appendChild(optionElement);
      });

      // Hide loading state and show container
      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
      liningContainer.style.display = 'flex';

      // Setup event handlers for new options
      this.setupLiningOptionHandlers();
      
      // Initialize price display for current fabric
      this.initializeLiningPricing();
      
      // Recalculate accordion height if it's currently open
      if (liningAccordion && liningAccordion.recalculateHeight) {
        liningAccordion.recalculateHeight();
      }
      
    } catch (error) {
      console.error('Error generating lining options:', error);
      this.showErrorState();
    }
  }

  createLiningOption(lining) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'image-option';
    button.dataset.value = lining.name;
    button.dataset.liningId = lining.id;
    button.dataset.weight = lining.weight_gsm;

    // Calculate lining price difference
    const priceDisplay = this.getLiningPriceDisplay(lining.id);

    button.innerHTML = `
      <img
        src="${lining.image}"
        alt="${lining.name}"
        width="100"
        height="100"
      >
      <div class="option-details">
        <div class="option-name">${lining.name}</div>
        <div class="option-price">（${priceDisplay}）</div>
      </div>
    `;

    return button;
  }

  /**
   * Calculate lining price display
   * @param {string} liningId - Lining ID
   * @returns {string} Formatted price display
   */
  getLiningPriceDisplay(liningId) {
    try {
      // If liningId is 'unlined', it's the base price
      if (liningId === 'unlined') {
        return this.formatPrice(0);
      }

      // Use existing price calculation logic from PricePreviewManager
      if (window.curtainProductOptions?.pricePreview?.calculator) {
        const pricePreview = window.curtainProductOptions.pricePreview;

        if (!pricePreview.isInitialized) {
          return this.formatPrice(0);
        }

        // Get current configuration
        const configData = pricePreview.collectCurrentConfiguration();

        // Check if we have minimum required data
        if (!pricePreview.hasMinimumRequiredData(configData)) {
          return this.formatPrice(0);
        }

        // Calculate price difference between this lining and unlined
        const currentFabricId = configData.fabricId;
        const fabricConfig = pricePreview.calculator.configLoader.getFabric(currentFabricId);
        
        if (!fabricConfig) {
          return this.formatPrice(0);
        }

        // Calculate material usage
        const headerConfig = pricePreview.calculator.configLoader.getHeaderType(configData.headerType);
        const materialUsage = pricePreview.calculator.calculateMaterialUsage(
          configData.widthInches,
          configData.lengthInches,
          headerConfig
        );

        // Calculate price for this lining
        const liningPrice = pricePreview.calculator.calculateFabricCost(
          materialUsage.meters,
          fabricConfig,
          liningId
        );

        // Calculate price for unlined
        const unlinedPrice = pricePreview.calculator.calculateFabricCost(
          materialUsage.meters,
          fabricConfig,
          'unlined'
        );

        // Calculate price difference
        const quantity = configData.curtainQuantity || 1;
        const priceDifference = (liningPrice - unlinedPrice) * quantity;

        return priceDifference > 0 ? `+${this.formatPrice(priceDifference * Shopify.currency.rate)}` : this.formatPrice(0);
      }

      return this.formatPrice(0);
    } catch (error) {
      console.warn('Error calculating lining price display:', error);
      return this.formatPrice(0);
    }
  }

  /**
   * Update lining price display for all options
   */
  updateLiningPriceDisplay() {
    try {
      const liningContainer = document.querySelector('.image-selector-container[data-property-name="Lining"]');
      if (!liningContainer) return;

      const options = liningContainer.querySelectorAll('.image-option');
      options.forEach((option) => {
        const liningId = option.dataset.liningId;
        const priceDisplay = this.getLiningPriceDisplay(liningId);

        const priceElement = option.querySelector('.option-price');
        if (priceElement) {
          priceElement.innerHTML = `（${priceDisplay}）`;
        }
      });
    } catch (error) {
      console.warn('Error updating lining price display:', error);
    }
  }

  /**
   * Format price using calculator's currency formatting
   * @param {number} amount - Price amount
   * @returns {string} Formatted price string
   */
  formatPrice(amount) {
    try {
      // First try to use calculator's formatPrice method
      if (window.curtainProductOptions?.pricePreview?.calculator) {
        const calculator = window.curtainProductOptions.pricePreview.calculator;
        if (calculator.formatPrice) {
          return calculator.formatPrice(amount);
        }
      }

      // Fallback to PricePreviewManager's getFormattedPrice method
      if (window.curtainProductOptions?.pricePreview?.getFormattedPrice) {
        return window.curtainProductOptions.pricePreview.getFormattedPrice(amount);
      }

      // Final fallback
      return `$${amount.toFixed(2)}`;
    } catch (error) {
      console.warn('Error formatting price:', error);
      return `$${amount.toFixed(2)}`;
    }
  }

  /**
   * Initialize lining pricing display for current fabric
   */
  initializeLiningPricing() {
    // Try to get current fabric from window object or default
    let currentFabricId = null;
    
    if (window.currentProductFabric && window.currentProductFabric.fabricId) {
      currentFabricId = window.currentProductFabric.fabricId;
    } else {
      // Fallback to first available fabric
      try {
        const fabrics = this.configLoader.getEnabledDefaultFabrics();
        const fabricIds = Object.keys(fabrics);
        if (fabricIds.length > 0) {
          currentFabricId = fabricIds[0];
        }
      } catch (error) {
        console.warn('Could not get default fabric for lining price initialization:', error);
      }
    }
    
    if (currentFabricId) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.updateLiningOptionsForFabric(currentFabricId);
      }, 100);
    } else {
      // Set fallback pricing if no fabric is available
      this.setFallbackLiningPricing();
    }
  }

  /**
   * Set fallback pricing when fabric information is not available
   */
  setFallbackLiningPricing() {
    const liningContainer = document.querySelector('.image-selector-container[data-property-name="Lining"]');
    if (!liningContainer) return;

    // Fallback pricing display removed - all pricing information is now shown in TOTAL PRICE section
  }

  updateLiningOptionsForFabric(fabricId) {
    if (!this.configLoader || !this.isInitialized) {
      return;
    }

    this.currentFabricId = fabricId;
    
    try {
      // Get fabric configuration
      const fabric = this.configLoader.getFabric(fabricId);
      if (!fabric) {
        console.warn(`Fabric ${fabricId} not found`);
        return;
      }

      // Find lining container
      const liningContainer = document.querySelector('.image-selector-container[data-property-name="Lining"]');
      if (!liningContainer) {
        return;
      }

      const liningOptions = liningContainer.querySelectorAll('.image-option');
      
      liningOptions.forEach(option => {
        const liningId = option.dataset.liningId;
        
        if (liningId === 'unlined') {
          // Unlined is always available
          option.style.display = 'flex';
          option.classList.remove('disabled');
        } else {
          // Check if fabric supports lining
          if (fabric.supports_lining) {
            option.style.display = 'flex';
            option.classList.remove('disabled');
          } else {
            // Hide or disable lining options for fabrics that don't support lining
            option.style.display = 'none';
            option.classList.add('disabled');
            
            // If this option was selected, deselect it and select unlined
            if (option.classList.contains('selected')) {
              option.classList.remove('selected');
              const unlinedOption = liningContainer.querySelector('.image-option[data-lining-id="unlined"]');
              if (unlinedOption) {
                unlinedOption.click();
              }
            }
          }
        }
      });

      // Show compatibility message
      this.showCompatibilityMessage(fabric.supports_lining);
      
      const liningAccordion = liningContainer.closest('.product-accordion');
      if (liningAccordion && liningAccordion.recalculateHeight) {
        requestAnimationFrame(() => {
          liningAccordion.recalculateHeight();
        });
      }
      
    } catch (error) {
      console.error('Error updating lining options for fabric:', error);
    }
  }

  showCompatibilityMessage(supportsLining) {
    const liningAccordion = document.querySelector('[data-option-group="lining"]')?.closest('.product-accordion');
    if (!liningAccordion) return;

    let messageContainer = liningAccordion.querySelector('.compatibility-message');
    
    if (!messageContainer) {
      messageContainer = document.createElement('div');
      messageContainer.className = 'compatibility-message';
      
      const liningContainer = liningAccordion.querySelector('.image-selector-container');
      if (liningContainer) {
        liningContainer.parentNode.insertBefore(messageContainer, liningContainer.nextSibling);
      }
    }

    if (!supportsLining) {
      messageContainer.innerHTML = '<p class="compatibility-warning">⚠️ Selected fabric does not support lining options. Only "Unlined" is available.</p>';
      messageContainer.style.display = 'block';
    } else {
      messageContainer.style.display = 'none';
    }
    
    if (liningAccordion && liningAccordion.recalculateHeight) {
      requestAnimationFrame(() => {
        liningAccordion.recalculateHeight();
      });
    }
  }

  setupLiningOptionHandlers() {
    // Find the lining accordion section by looking for the button with data-option-group="lining"
    const liningButton = document.querySelector('[data-option-group="lining"]');
    if (!liningButton) {
      console.error('Lining button not found in setupLiningOptionHandlers');
      return;
    }
    
    // Get the parent accordion and then find the content elements
    const liningAccordion = liningButton.closest('.product-accordion');
    if (!liningAccordion) {
      console.error('Lining accordion not found in setupLiningOptionHandlers');
      return;
    }
    
    const liningContainer = liningAccordion.querySelector('.image-selector-container');
    const hiddenInput = liningAccordion.querySelector('input[name="properties[Lining]"]');
    const liningIdInput = liningAccordion.querySelector('input[name="properties[_Lining ID]"]');
    const selectedOptionDisplay = liningButton.querySelector('.selected-option-display');

    // Set form attributes for hidden inputs
    const container = document.querySelector('.custom-product-options-container');
    const productFormId = container?.dataset.productFormId || '';
    if (hiddenInput && productFormId) {
      hiddenInput.setAttribute('form', productFormId);
    }
    if (liningIdInput && productFormId) {
      liningIdInput.setAttribute('form', productFormId);
    }

    if (!liningContainer) return;

    const options = liningContainer.querySelectorAll('.image-option');
    options.forEach((option) => {
      option.addEventListener('click', () => {
        // Skip if option is disabled
        if (option.classList.contains('disabled')) {
          return;
        }
        
        // Remove selection from all options
        options.forEach((opt) => opt.classList.remove('selected'));
        
        // Select current option
        option.classList.add('selected');
        
        // Update hidden inputs
        if (hiddenInput) {
          hiddenInput.value = option.dataset.value;
        }
        if (liningIdInput) {
          liningIdInput.value = option.dataset.liningId;
        }
        
        // Update display
        if (selectedOptionDisplay) {
          selectedOptionDisplay.textContent = option.dataset.value;
        }
        
        const configChangeEvent = new CustomEvent('configurationChanged', {
          detail: { type: 'lining', value: option.dataset.value }
        });
        document.dispatchEvent(configChangeEvent);
        
      });
    });

    // Set default selection to "Unlined"
    const defaultOption = liningContainer.querySelector('.image-option[data-lining-id="unlined"]');
    if (defaultOption && !liningContainer.querySelector('.image-option.selected')) {
      defaultOption.click();
    }
  }

  showErrorState() {
    // Find the lining accordion section by looking for the button with data-option-group="lining"
    const liningButton = document.querySelector('[data-option-group="lining"]');
    if (!liningButton) {
      console.error('Lining button not found in showErrorState');
      return;
    }
    
    // Get the parent accordion and then find the loading element
    const liningAccordion = liningButton.closest('.product-accordion');
    if (!liningAccordion) {
      console.error('Lining accordion not found in showErrorState');
      return;
    }
    
    const loadingElement = liningAccordion.querySelector('.lining-loading');
    if (loadingElement) {
      loadingElement.innerHTML = '<p style="color: #d32f2f;">Error loading lining options. Please refresh the page.</p>';
    }
  }
}

// Dynamic Fabric Compatibility Manager
class DynamicFabricCompatibility {
  constructor() {
    this.configLoader = null;
    this.isInitialized = false;
    this.currentFabricId = null;
    this.currentFabricConfig = null;
  }

  async initialize() {
    try {
      // Use centralized configuration initialization
      this.configLoader = await window.curtainConfigLoader.getInitializationPromise();
      
      this.setupProductFabricListener();
      this.setupFabricSelectionListener();
      this.isInitialized = true;
      
      // Handle any pending fabric updates that occurred before initialization
      if (this._pendingFabricUpdate && this.currentFabricConfig) {
        this.updateCompatibilityUI();
        this._pendingFabricUpdate = false;
      }
      
    } catch (error) {
      console.error('Error initializing dynamic fabric compatibility:', error);
    }
  }

  setupProductFabricListener() {
    document.addEventListener('productFabricLoaded', (event) => {
      const fabricInfo = event.detail;
      this.handleProductFabricLoad(fabricInfo);
    });

    if (window.currentProductFabric) {
      this.handleProductFabricLoad(window.currentProductFabric);
    }
  }

  setupFabricSelectionListener() {
    // Listen for fabric selection changes from Shopify product variants
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantSelectors = productForm.querySelectorAll('input[name="id"], select[name="id"]');
      
      variantSelectors.forEach(selector => {
        selector.addEventListener('change', (event) => {
          this.handleFabricChange(event.target.value);
        });
      });

      // Also listen for radio button changes if using radio buttons for variants
      const radioButtons = productForm.querySelectorAll('input[type="radio"][name="id"]');
      radioButtons.forEach(radio => {
        radio.addEventListener('change', (event) => {
          if (event.target.checked) {
            this.handleFabricChange(event.target.value);
          }
        });
      });
    }
  }

  handleProductFabricLoad(fabricInfo) {
    if (!this.configLoader) {
      // If config loader is not ready, defer execution
      // Set up a one-time listener for when config is ready
      if (!this._deferredFabricInfo) {
        this._deferredFabricInfo = fabricInfo;
        window.curtainConfigLoader.getInitializationPromise().then(() => {
          if (this._deferredFabricInfo) {
            this.handleProductFabricLoad(this._deferredFabricInfo);
            this._deferredFabricInfo = null;
          }
        }).catch(error => {
          console.error('Error waiting for configurations in deferred fabric load:', error);
        });
      }
      return;
    }

    try {
      this.currentFabricId = fabricInfo.fabricId;
      this.currentFabricConfig = this.configLoader.getFabric(this.currentFabricId);
      
      if (!this.currentFabricConfig) {
        console.warn(`Fabric ${this.currentFabricId} not found in config, using fallback`);
        const fabrics = this.configLoader.getEnabledDefaultFabrics();
        this.currentFabricId = Object.keys(fabrics)[0];
        this.currentFabricConfig = fabrics[this.currentFabricId];
      }

      // Only update UI if initialization is complete, otherwise defer
      if (this.isInitialized) {
        this.updateCompatibilityUI();
      } else {
        // Store for later use when initialization completes
        this._pendingFabricUpdate = true;
      }
      
      const fabricChangeEvent = new CustomEvent('fabricChanged', {
        detail: { 
          fabricId: this.currentFabricId,
          fabricConfig: this.currentFabricConfig,
          source: 'product'
        }
      });
      document.dispatchEvent(fabricChangeEvent);
      
    } catch (error) {
      console.error('Error handling product fabric load:', error);
    }
  }

  handleFabricChange(variantId) {
    if (!this.configLoader || !this.isInitialized) {
      return;
    }

    try {
      const fabricId = this.getFabricIdFromVariant(variantId);
      
      if (fabricId && fabricId !== this.currentFabricId) {
        this.currentFabricId = fabricId;
        this.currentFabricConfig = this.configLoader.getFabric(fabricId);
        
        this.updateCompatibilityUI();
        
        const fabricChangeEvent = new CustomEvent('fabricChanged', {
          detail: { 
            fabricId: fabricId, 
            variantId: variantId,
            fabricConfig: this.currentFabricConfig,
            source: 'variant'
          }
        });
        document.dispatchEvent(fabricChangeEvent);
        
      }
    } catch (error) {
      console.error('Error handling fabric change:', error);
    }
  }

  updateCompatibilityUI() {
    if (!this.currentFabricConfig) {
      return;
    }

    this.updateLiningCompatibility();
    
    this.updateHeatSettingCompatibility();
  }

  updateLiningCompatibility() {
    if (window.curtainProductOptions && window.curtainProductOptions.liningSelector) {
      const liningSelector = window.curtainProductOptions.liningSelector;
      if (liningSelector.updateLiningOptionsForFabric) {
        liningSelector.updateLiningOptionsForFabric(this.currentFabricId);
      }
    }
  }

  updateHeatSettingCompatibility() {
    if (window.curtainProductOptions && window.curtainProductOptions.heatSettingSelector) {
      const heatSettingSelector = window.curtainProductOptions.heatSettingSelector;
      if (heatSettingSelector.updateCompatibilityForFabric) {
        heatSettingSelector.updateCompatibilityForFabric(this.currentFabricConfig);
      }
    }
  }

  getFabricIdFromVariant(variantId) {
    return window.currentProductFabric?.fabricId || this.currentFabricId;
  }

  getCurrentFabricId() {
    return this.currentFabricId;
  }

  getCurrentFabricConfig() {
    return this.currentFabricConfig;
  }
}

// Price Preview Manager
class PricePreviewManager {
  constructor() {
    this.isInitialized = false;
    this.configLoader = null;
    this.calculator = null;
    
    // DOM elements
    this.container = null;
    this.loadingState = null;
    this.errorState = null;
    this.selectionSummary = null;
    
    // Price display elements
    this.totalPriceAmount = null;

    // Selection value elements
    this.colorValue = null;
    this.headerValue = null;
    this.sizeValue = null;
    this.liningValue = null;
    this.weightValue = null;
    this.heatSettingValue = null;
    this.tieBacksValue = null;
    this.quantityValue = null;

    // Item elements for conditional display
    this.heatSettingItem = null;
    this.tieBacksItem = null;
    
    // Update timeout
    this.updateTimeout = null;
  }

  async initialize() {
    try {
      // Use centralized configuration initialization
      this.configLoader = await window.curtainConfigLoader.getInitializationPromise();
      
      // Wait for calculator to be ready
      await this.waitForCalculator();
      
      this.initializeDOMElements();
      this.setupShopifyPriceChangeDetection();
      this.isInitialized = true;
      
      // Initialize display with base price
      this.updatePreview();
      
    } catch (error) {
      console.error('Error initializing price preview manager:', error);
      this.showError('Failed to initialize price preview');
    }
  }

  setupShopifyPriceChangeDetection() {
    const sectionId = this.getSectionId();
    if (sectionId) {
      // const productPriceContainer = document.getElementById(`price-${sectionId}`);
      // if (productPriceContainer) {
      //   const observer = new MutationObserver((mutations) => {
      //     mutations.forEach((mutation) => {
      //       if (mutation.type === 'childList' || mutation.type === 'characterData') {
      //         if (!this._priceUpdating) {
      //           this._lastShopifyPriceUpdate = Date.now();
      //           setTimeout(() => {
      //             if (this.isInitialized) {
      //               this.updatePreview();
      //             }
      //           }, 300);
      //         }
      //       }
      //     });
      //   });

      //   observer.observe(productPriceContainer, {
      //     childList: true,
      //     subtree: true,
      //     characterData: true
      //   });

      //   this._priceObserver = observer;
      // }
    }

    document.addEventListener('product:variant-change', (event) => {
      this._lastShopifyPriceUpdate = Date.now();
    });
  }

  waitForCalculator() {
    return new Promise((resolve, reject) => {
      if (window.curtainCalculator && window.curtainCalculator instanceof Object) {
        this.calculator = window.curtainCalculator;
        resolve();
        return;
      }

      const checkCalculator = () => {
        if (window.curtainCalculator && window.curtainCalculator instanceof Object) {
          this.calculator = window.curtainCalculator;
          resolve();
        } else {
          setTimeout(checkCalculator, 100);
        }
      };

      setTimeout(() => {
        reject(new Error('Calculator initialization timeout'));
      }, 5000);

      checkCalculator();
    });
  }

  initializeDOMElements() {
    // Main container
    this.container = document.querySelector('.price-preview-container');
    if (!this.container) {
      throw new Error('Price preview container not found');
    }

    // State elements
    this.loadingState = this.container.querySelector('.price-preview-loading');
    this.errorState = this.container.querySelector('.price-preview-error');
    this.selectionSummary = this.container.querySelector('.selection-summary');

    // Price display elements
    this.totalPriceAmount = this.container.querySelector('.total-price-amount');

    // Save original prices immediately after DOM elements are found
    this.saveOriginalPrices();
    
    // Selection value elements
    this.colorValue = this.container.querySelector('.color-value');
    this.headerValue = this.container.querySelector('.header-value');
    this.sizeValue = this.container.querySelector('.size-value');
    this.liningValue = this.container.querySelector('.lining-value');
    this.weightValue = this.container.querySelector('.weight-value');
    this.heatSettingValue = this.container.querySelector('.heat-setting-value');
    this.tieBacksValue = this.container.querySelector('.tie-backs-value');
    this.quantityValue = this.container.querySelector('.quantity-value');

    // Item elements for conditional display
    this.heatSettingItem = this.container.querySelector('.heat-setting-item');
    this.tieBacksItem = this.container.querySelector('.tie-backs-item');
  }

  updatePreview() {
    if (!this.isInitialized || !this.calculator || !this.configLoader) {
      return;
    }

    if (this._priceUpdating) {
      setTimeout(() => this.updatePreview(), 50);
      return;
    }

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    const now = Date.now();
    const delay = (this._lastShopifyPriceUpdate && (now - this._lastShopifyPriceUpdate) < 500) ? 300 : 100;
    
    this.updateTimeout = setTimeout(() => {
      this.performUpdate();
    }, delay);
  }

  async performUpdate() {
    try {
      this.showLoading();
      
      const configData = this.collectCurrentConfiguration();
      
      if (!this.hasMinimumRequiredData(configData)) {
        if (this.hasBasicConfigurationData(configData)) {
          this.showPartialPrice(configData);
        } else {
          this.showBaseProductPrice(configData);
        }
        return;
      }

      const calculationResult = this.calculator.calculateTotal(configData);
      
      if (calculationResult.success) {
        // Log base product price information for debugging
        if (this.calculator.getBaseProductPriceInfo) {
          const basePriceInfo = this.calculator.getBaseProductPriceInfo();
        }
        
        this.updatePriceDisplay(calculationResult, configData);
      } else {
        this.showError(calculationResult.errors.join(', '));
      }
    } catch (error) {
      console.error('Error updating price preview:', error);
      this.showError('Error updating price preview');
    }
  }

  collectCurrentConfiguration() {
    const container = document.querySelector('.custom-product-options-container');
    if (!container) return {};

    const widthInput = container.querySelector('input[name="properties[Panel Width (in)]"]');
    const lengthInput = container.querySelector('input[name="properties[Panel Length (in)]"]');
    
    const headerInput = container.querySelector('input[name="properties[Header]"]');
    
    const liningInput = container.querySelector('input[name="properties[_Lining ID]"]');
    
    const heatSettingInput = container.querySelector('input[name="properties[Heat Setting]"]');
    
    const tieBacksInput = container.querySelector('input[name="properties[Tie Backs]"]');
    const tieBacksQuantityInput = container.querySelector('input[name="properties[_Tie Backs Quantity]"]');

    const fabricId = window.currentProductFabric?.fabricId || 'luxe_velvet';
    
    const productForm = document.getElementById(container.dataset.productFormId);
    const quantityInput = productForm?.querySelector('input[name="quantity"]');
    const curtainQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    return {
      widthInches: widthInput ? parseFloat(widthInput.value) || 0 : 0,
      lengthInches: lengthInput ? parseFloat(lengthInput.value) || 0 : 0,
      headerType: this.getHeaderTypeFromValue(headerInput?.value),
      fabricId: fabricId,
      liningId: liningInput?.value || 'unlined',
      heatSetting: heatSettingInput?.value === 'Yes',
      tieCount: tieBacksQuantityInput ? parseInt(tieBacksQuantityInput.value) || 0 : 0,
      curtainQuantity: curtainQuantity,
      
      selectedHeader: headerInput?.value,
      selectedLining: container.querySelector('input[name="properties[Lining]"]')?.value,
      projectName: container.querySelector('input[name="properties[Project Name]"]')?.value
    };
  }

  getHeaderTypeFromValue(headerValue) {
    if (!headerValue || !this.configLoader) return null;
    
    const headerTypes = this.configLoader.getEnabledHeaderTypes();
    
    for (const [id, header] of Object.entries(headerTypes)) {
      if (header.name === headerValue) {
        return id;
      }
    }
    
    return null;
  }

  hasMinimumRequiredData(configData) {
    return configData.widthInches > 0 && 
           configData.lengthInches > 0 && 
           configData.headerType && 
           configData.fabricId;
  }

  hasBasicConfigurationData(configData) {
    return configData.fabricId && 
           (configData.widthInches > 0 || configData.lengthInches > 0 || configData.headerType);
  }

  updatePriceDisplay(calculationResult, configData) {
    try {
      // The calculator now returns the complete total price including quantity
      const totalPrice = calculationResult.total || 0;

      // Update all price displays (both total price and product price)
      this.updateAllPriceDisplays(totalPrice);

      // Update selection values
      this.updateSelectionValues(configData, calculationResult);

      // Show/hide optional items
      this.updateOptionalItems(calculationResult, configData);

      this.showSelectionSummary();
      
      
    } catch (error) {
      console.error('Error updating price display:', error);
      this.showError('Error updating price display');
    }
  }

  updateSelectionValues(configData, calculationResult) {
    try {
      // Update color (current selected variant color)
      if (this.colorValue) {
        const currentVariantColor = this.getCurrentVariantColor();
        this.colorValue.textContent = currentVariantColor || 'Not selected';
      }

      // Update header
      if (this.headerValue) {
        this.headerValue.textContent = configData.selectedHeader || '-';
      }

      // Update size
      if (this.sizeValue) {
        if (configData.widthInches > 0 && configData.lengthInches > 0) {
          const projectText = configData.projectName ? ` (${configData.projectName})` : '';
          this.sizeValue.textContent = `${configData.widthInches}"W x ${configData.lengthInches}"L${projectText}`;
        } else {
          this.sizeValue.textContent = '-';
        }
      }

      // Update lining
      if (this.liningValue) {
        if (configData.liningId && configData.liningId !== 'unlined') {
          const liningOptions = this.configLoader.getLiningOptions();
          const liningConfig = liningOptions[configData.liningId];
          this.liningValue.textContent = liningConfig ? liningConfig.name : (configData.selectedLining || 'Unknown lining');
        } else {
          this.liningValue.textContent = 'Unlined';
        }
      }

      // Update weight
      if (this.weightValue) {
        const weight = this.calculator.calculateWeight(configData, configData.curtainQuantity);
        // Convert kg to pounds (1 kg = 2.20462 lbs, or 1000g ÷ 454g/lb ≈ 2.2046)
        const weightInPounds = weight * 2.20462;
        this.weightValue.textContent = `~${weightInPounds.toFixed(2)} lb (per panel)`;
      }

      // Update quantity
      if (this.quantityValue) {
        this.quantityValue.textContent = `${configData.curtainQuantity} panel${configData.curtainQuantity > 1 ? 's' : ''}`;
      }

    } catch (error) {
      console.error('Error updating selection values:', error);
    }
  }

  // Helper method to get current selected variant color
  getCurrentVariantColor() {
    try {
      // First try to get from product form
      const container = document.querySelector('.custom-product-options-container');
      if (container) {
        const productForm = document.getElementById(container.dataset.productFormId);
        if (productForm) {
          // Look for variant selector (could be select, radio buttons, or hidden input)
          const variantSelector = productForm.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]');
          
          if (variantSelector && variantSelector.value) {
            const selectedVariantId = variantSelector.value;
            
            // Try to get variant info from window.product if available
            if (window.product && window.product.variants) {
              const selectedVariant = window.product.variants.find(variant => 
                variant.id.toString() === selectedVariantId.toString()
              );
              
              if (selectedVariant) {
                // Return the first option value which should be the color
                const color = selectedVariant.option1 || selectedVariant.title;
                return color;
              }
            }
          }
        }
      }

      // Fallback: try to get from variant picker elements
      const variantPickers = document.querySelectorAll('.product-form__input input[type="radio"]:checked, .variant-picker input[type="radio"]:checked');
      if (variantPickers.length > 0) {
        // Get the first checked radio button (usually color)
        const colorPicker = variantPickers[0];
        const label = document.querySelector(`label[for="${colorPicker.id}"]`);
        if (label) {
          const color = label.textContent.trim();
          return color;
        }
        const color = colorPicker.value;
        return color;
      }

      // Another fallback: look for selected variant in any form
      const allForms = document.querySelectorAll('form[action*="/cart/add"]');
      for (const form of allForms) {
        const variantInput = form.querySelector('input[name="id"], select[name="id"]');
        if (variantInput && variantInput.value) {
          // Try to find associated color information
          const colorInputs = form.querySelectorAll('input[name*="color"], input[name*="Color"]');
          for (const colorInput of colorInputs) {
            if (colorInput.checked || colorInput.value) {
              const color = colorInput.value || colorInput.getAttribute('data-value');
              return color;
            }
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting current variant color:', error);
      return null;
    }
  }

  updateOptionalItems(calculationResult, configData) {
    // Heat setting
    if (this.heatSettingItem) {
      if (calculationResult.breakdown.heatSettingCost > 0) {
        this.heatSettingItem.style.display = 'flex';
        if (this.heatSettingValue) {
          this.heatSettingValue.textContent = 'Yes';
        }
      } else {
        this.heatSettingItem.style.display = 'none';
      }
    }

    // Tie backs
    if (this.tieBacksItem) {
      if (calculationResult.breakdown.tieCost > 0) {
        this.tieBacksItem.style.display = 'flex';
        if (this.tieBacksValue) {
          this.tieBacksValue.textContent = `${configData.tieCount} tie back${configData.tieCount > 1 ? 's' : ''}`;
        }
      } else {
        this.tieBacksItem.style.display = 'none';
      }
    }
  }

  showLoading() {
    if (this.loadingState) this.loadingState.style.display = 'block';
    if (this.errorState) this.errorState.style.display = 'none';
    if (this.selectionSummary) this.selectionSummary.style.display = 'none';
  }

  showError(message) {
    if (this.loadingState) this.loadingState.style.display = 'none';
    if (this.selectionSummary) this.selectionSummary.style.display = 'none';
    if (this.errorState) {
      this.errorState.style.display = 'block';
      const errorMessage = this.errorState.querySelector('.error-message');
      if (errorMessage) {
        errorMessage.textContent = message || 'Error calculating price, please check your selections.';
      }
    }
  }

  showIncompleteData() {
    if (this.loadingState) this.loadingState.style.display = 'none';
    if (this.selectionSummary) this.selectionSummary.style.display = 'none';
    if (this.errorState) {
      this.errorState.style.display = 'block';
      const errorMessage = this.errorState.querySelector('.error-message');
      if (errorMessage) {
        const configData = this.collectCurrentConfiguration();
        const missingItems = [];
        
        if (!configData.headerType) {
          missingItems.push('Header type');
        }
        if (configData.widthInches <= 0 || configData.lengthInches <= 0) {
          if (configData.widthInches <= 0 && configData.lengthInches <= 0) {
            missingItems.push('Width and Length');
          } else if (configData.widthInches <= 0) {
            missingItems.push('Width');
          } else {
            missingItems.push('Length');
          }
        }
        
        if (missingItems.length > 0) {
          errorMessage.textContent = `Please complete: ${missingItems.join(', ')} to view accurate pricing.`;
        } else {
          errorMessage.textContent = 'Please complete all required selections to view price preview.';
        }
      }
    }
  }

  showBaseProductPrice(configData) {
    try {
      let totalPrice = this.getBaseProductPrice(configData);
      
      // If we have header selected, add header fee manually
      if (configData.headerType && configData.fabricId && this.configLoader) {
        try {
          const headerTypes = this.configLoader.getEnabledHeaderTypes();
          const headerConfig = headerTypes[configData.headerType];
          
          if (headerConfig && headerConfig.fixed_fee) {
            const headerFee = parseFloat(headerConfig.fixed_fee) || 0;
            const quantity = configData.curtainQuantity || 1;
            totalPrice = totalPrice + (headerFee * Shopify.currency.rate * quantity);
          }
        } catch (error) {
          console.error('Error adding header fee in showBaseProductPrice:', error);
        }
      }
      
      this.updateAllPriceDisplays(totalPrice);

      this.updateBaseSelectionValues(configData);

      this.showSelectionSummary();
      
    } catch (error) {
      console.error('Error showing base product price:', error);
      this.showIncompleteData();
    }
  }

  getBaseProductPrice(configData) {
    if (window.currentProductPricing && window.currentProductPricing.basePriceDecimal) {
      const basePrice = parseFloat(window.currentProductPricing.basePriceDecimal);
      const quantity = configData.curtainQuantity || 1;
      return basePrice * quantity;
    }

    if (this.calculator && this.calculator.getBaseProductPriceInfo) {
      const basePriceInfo = this.calculator.getBaseProductPriceInfo();
      if (basePriceInfo && basePriceInfo.priceDecimal) {
        const quantity = configData.curtainQuantity || 1;
        return basePriceInfo.priceDecimal * quantity;
      }
    }

    return 0;
  }

  updateBaseSelectionValues(configData) {
    try {
      
      if (this.colorValue) {
        const currentVariantColor = this.getCurrentVariantColor();
        this.colorValue.textContent = currentVariantColor || 'Selected';
      }

      if (this.headerValue) {
        this.headerValue.textContent = configData.selectedHeader || 'Please select';
      }

      if (this.sizeValue) {
        if (configData.widthInches > 0 && configData.lengthInches > 0) {
          const projectText = configData.projectName ? ` (${configData.projectName})` : '';
          this.sizeValue.textContent = `${configData.widthInches}"W x ${configData.lengthInches}"L${projectText}`;
        } else {
          this.sizeValue.textContent = 'Please enter dimensions';
        }
      }

      if (this.liningValue) {
        if (configData.liningId && configData.liningId !== 'unlined') {
          const liningOptions = this.configLoader.getLiningOptions();
          const liningConfig = liningOptions[configData.liningId];
          this.liningValue.textContent = liningConfig ? liningConfig.name : (configData.selectedLining || 'Selected');
        } else {
          this.liningValue.textContent = configData.selectedLining || 'Unlined';
        }
      }

      if (this.weightValue) {
        if (configData.widthInches > 0 && configData.lengthInches > 0 && this.calculator) {
          try {
            const weight = this.calculator.calculateWeight(configData, configData.curtainQuantity);
            // Convert kg to pounds (1 kg = 2.20462 lbs)
            const weightInPounds = weight * 2.20462;
            this.weightValue.textContent = `~${weightInPounds.toFixed(2)} lb (per panel)`;
          } catch (error) {
            this.weightValue.textContent = 'Please complete size selection';
          }
        } else {
          this.weightValue.textContent = 'Please complete size selection';
        }
      }

      if (this.quantityValue) {
        this.quantityValue.textContent = `${configData.curtainQuantity} panel${configData.curtainQuantity > 1 ? 's' : ''}`;
      }

      if (this.heatSettingItem) {
        this.heatSettingItem.style.display = 'none';
      }
      if (this.tieBacksItem) {
        this.tieBacksItem.style.display = 'none';
      }

         } catch (error) {
       console.error('Error updating base selection values:', error);
     }
   }

  showPartialPrice(configData) {
    try {
      const baseProductPrice = this.getBaseProductPrice(configData);
      let totalPrice = baseProductPrice;
      let headerFeeAdded = false;

      // If we have size data, try to calculate with the curtain calculator
      if (configData.widthInches > 0 && configData.lengthInches > 0 && configData.fabricId && this.calculator) {
        try {
          const simplifiedConfig = {
            ...configData,
            headerType: configData.headerType || 'tab_top', 
            liningId: configData.liningId || 'unlined',
            heatSetting: false,
            tieCount: 0
          };

          const partialResult = this.calculator.calculateTotal(simplifiedConfig);
          if (partialResult.success) {
            // The calculator now returns the complete total price including quantity
            totalPrice = partialResult.total || 0;
            headerFeeAdded = true;
          }
        } catch (error) {
          // Keep base product price if calculation fails
        }
      } 
      // If no size but we have header selected, add header fee manually
      else if (configData.headerType && configData.fabricId && this.configLoader) {
        try {
          const headerTypes = this.configLoader.getEnabledHeaderTypes();
          const headerConfig = headerTypes[configData.headerType];
          
          if (headerConfig && headerConfig.fixed_fee) {
            const headerFee = parseFloat(headerConfig.fixed_fee) || 0;
            const quantity = configData.curtainQuantity || 1;
            totalPrice = baseProductPrice + (headerFee * Shopify.currency.rate * quantity);
            headerFeeAdded = true;
          }
        } catch (error) {
          console.error('Error adding header fee:', error);
        }
      }

      this.updateAllPriceDisplays(totalPrice);

      this.updateBaseSelectionValues(configData);

      this.showSelectionSummary();
      
    } catch (error) {
      console.error('Error showing partial price:', error);
      this.showBaseProductPrice(configData);
    }
  }



  showSelectionSummary() {
    if (this.loadingState) this.loadingState.style.display = 'none';
    if (this.errorState) this.errorState.style.display = 'none';
    if (this.selectionSummary) this.selectionSummary.style.display = 'block';
  }

  formatPrice(amount) {
    if (typeof amount !== 'number') return this.getFormattedPrice(0);
    
    try {
      // First try to use calculator's formatPrice method
      if (this.calculator && this.calculator.formatPrice) {
        return this.calculator.formatPrice(amount);
      }

      // Fallback to getFormattedPrice method
      return this.getFormattedPrice(amount);
    } catch (error) {
      console.warn('Error formatting price:', error);
      return this.getFormattedPrice(amount);
    }
  }

  updateAllPriceDisplays(totalPrice) {
    if (this.totalPriceAmount) {
      this.totalPriceAmount.innerHTML = this.formatPrice(totalPrice);
    }

    this.updateProductPriceDisplay(totalPrice);
  }

  getSectionId() {
    try {
      // Method 1: Try to get from custom product options container
      const container = document.querySelector('.custom-product-options-container');
      if (container && container.dataset.productFormId) {
        const formId = container.dataset.productFormId;
        // Extract section ID from form ID like "product-form-template--20963975733504__main"
        const match = formId.match(/product-form-(.+)/);
        if (match) {
          return match[1];
        }
      }
      
      // Method 2: Try to find existing price element with section ID
      const existingPriceElement = document.querySelector('[id^="price-template--"]');
      if (existingPriceElement) {
        const match = existingPriceElement.id.match(/price-(.+)/);
        if (match) {
          return match[1];
        }
      }
      
      // Method 3: Try to get from ProductInfo container
      const productInfoContainer = document.querySelector('[id^="ProductInfo-template--"]');
      if (productInfoContainer) {
        const match = productInfoContainer.id.match(/ProductInfo-(.+)/);
        if (match) {
          return match[1];
        }
      }
      
      // Method 4: Try to get from variant selects
      const variantSelects = document.querySelector('[id^="variant-selects-template--"]');
      if (variantSelects) {
        const match = variantSelects.id.match(/variant-selects-(.+)/);
        if (match) {
          return match[1];
        }
      }
      
      // Method 5: Last resort - try to get from any element with template section ID
      const templateElement = document.querySelector('[id*="template--"][id*="__main"]');
      if (templateElement) {
        const match = templateElement.id.match(/template--[\d]+__[\w-]+/);
        if (match) {
          return match[0];
        }
      }
      
      return null;
      
    } catch (error) {
      console.error('Error getting section ID:', error);
      return null;
    }
  }

  updateProductPriceDisplay(totalPrice) {
    if (!this.shouldUpdateProductPrice()) {
      return;
    }

    const formattedPrice = this.getFormattedPrice(totalPrice);
    
    this._priceUpdating = true;
    
    try {
      const sectionId = this.getSectionId();
      
      if (sectionId) {
        const productPriceContainer = document.getElementById(`price-${sectionId}`);
        if (productPriceContainer) {
          const priceElements = productPriceContainer.querySelectorAll('.amount:not(del .amount)');
          priceElements.forEach(element => {
            if (!this.isElementInExcludedArea(element)) {
              element.textContent = formattedPrice;
              element.dataset.customPrice = 'true';
            }
          });
          
          return; 
        }
      }
      
      const productInfoContainer = document.querySelector('#ProductInfo-template--20963975733504__main, .product-information--inner, .product-title-container');
      if (productInfoContainer) {
        const priceElements = productInfoContainer.querySelectorAll('.price .amount:not(del .amount)');
        priceElements.forEach(element => {
          if (!this.isElementInExcludedArea(element)) {
            element.textContent = formattedPrice;
            element.dataset.customPrice = 'true';
          }
        });
        
        return; 
      }
      
      const fallbackSelectors = [
        '.product-price-container .amount:not(del .amount)',
        '.product-title-container .amount:not(del .amount)',
        '.product-information--inner .amount:not(del .amount)',
        '[data-price] .amount:not(del .amount)'
      ];

      fallbackSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          if (!this.isElementInExcludedArea(element)) {
            element.textContent = formattedPrice;
            element.dataset.customPrice = 'true';
          }
        });
      });

    } finally {
      setTimeout(() => {
        this._priceUpdating = false;
      }, 200);
    }
  }

  shouldUpdateProductPrice() {
    if (this._priceUpdating) {
      return false;
    }

    const now = Date.now();
    if (this._lastShopifyPriceUpdate && (now - this._lastShopifyPriceUpdate) < 300) {
      return false;
    }

    return true;
  }

  isElementInExcludedArea(element) {
    return element.closest('.price-preview-container') || 
           element.closest('.cart-drawer') || 
           element.closest('[id*="cart"]') ||
           element.closest('.quick-shop') ||
           element.closest('.product-card');
  }

  getFormattedPrice(amount) {
    if (this.originalPrices && this.originalPrices.size > 0) {
      const firstOriginalPrice = Array.from(this.originalPrices.values())[0];

      if (firstOriginalPrice) {
        // Always use English "From" prefix regardless of original language
        if (firstOriginalPrice.includes('$')) {
          return `$${amount.toFixed(2)}`;
        } else if (firstOriginalPrice.includes('¥')) {
          return `¥${amount.toFixed(2)}`;
        } else if (firstOriginalPrice.includes('€')) {
          return `€${amount.toFixed(2)}`;
        } else if (firstOriginalPrice.includes('£')) {
          return `£${amount.toFixed(2)}`;
        }
      }
    }

    return `$${amount.toFixed(2)}`;
  }

  saveOriginalPrices() {
    this.originalPrices = new Map();
    
    // Get section ID for precise targeting
    const sectionId = this.getSectionId();
    
    if (sectionId) {
      // Save prices from the specific product price container
      const productPriceContainer = document.getElementById(`price-${sectionId}`);
      if (productPriceContainer) {
        const priceElements = productPriceContainer.querySelectorAll('.amount:not(del .amount)');
        priceElements.forEach((element, index) => {
          this.originalPrices.set(`price-container-${index}`, element.textContent);
        });
        
        return; // Exit early if we found the specific container
      }
    }
    
    // Fallback: Save prices from product information area only
    const productInfoContainer = document.querySelector('.product-information--inner, .product-title-container');
    if (productInfoContainer) {
      const priceElements = productInfoContainer.querySelectorAll('.price .amount:not(del .amount)');
      priceElements.forEach((element, index) => {
        this.originalPrices.set(`product-info-${index}`, element.textContent);
      });
    }

    // Additional fallback: Limited selectors that won't affect cart
    const limitedSelectors = [
      '.product-price-container .amount:not(del .amount)',
      '.product-title-container .amount:not(del .amount)'
    ];

    limitedSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element, index) => {
        if (!element.closest('.price-preview-container') && 
            !element.closest('.cart-drawer') && 
            !element.closest('[id*="cart"]')) {
          this.originalPrices.set(`${selector}-${index}`, element.textContent);
        }
      });
    });

  }

  restoreOriginalPrices() {
    if (!this.originalPrices) return;

    const sectionId = this.getSectionId();
    
    this.originalPrices.forEach((originalText, key) => {
      if (key.startsWith('price-container-')) {
        // Restore prices in the specific price container
        if (sectionId) {
          const productPriceContainer = document.getElementById(`price-${sectionId}`);
          if (productPriceContainer) {
            const index = parseInt(key.split('-')[2]);
            const priceElements = productPriceContainer.querySelectorAll('.amount:not(del .amount)');
            if (priceElements[index]) {
              priceElements[index].textContent = originalText;
            }
          }
        }
      } else if (key.startsWith('product-info-')) {
        // Restore prices in product info area
        const productInfoContainer = document.querySelector('.product-information--inner, .product-title-container');
        if (productInfoContainer) {
          const index = parseInt(key.split('-')[2]);
          const priceElements = productInfoContainer.querySelectorAll('.price .amount:not(del .amount)');
          if (priceElements[index]) {
            priceElements[index].textContent = originalText;
          }
        }
      } else {
        // Handle other saved prices (fallback selectors)
        const parts = key.split('-');
        const index = parseInt(parts[parts.length - 1]);
        const selector = parts.slice(0, -1).join('-');
        const elements = document.querySelectorAll(selector);
        if (elements[index] && 
            !elements[index].closest('.price-preview-container') &&
            !elements[index].closest('.cart-drawer') &&
            !elements[index].closest('[id*="cart"]')) {
          elements[index].textContent = originalText;
        }
      }
    });

  }

  triggerUpdate() {
    this.updatePreview();
  }

  cleanup() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    if (this._priceObserver) {
      this._priceObserver.disconnect();
    }

    this.restoreOriginalPrices();
  }
}

// Dependency Manager - Controls the visibility and interaction of configuration sections based on prerequisites
class DependencyManager {
  constructor() {
    this.isInitialized = false;
    this.dependencies = {
      'size': ['header'], // Size depends on Header selection
      'lining': ['header', 'size'], // Lining depends on Header and Size
      'heat-setting': ['header', 'size'], // Heat Setting depends on Header and Size
      'ties': ['header', 'size'] // Tie Backs depend on Header and Size
    };
  }

  async initialize() {
    this.isInitialized = true;
    this.updateDependencies();  
  }

  updateDependencies() {
    if (!this.isInitialized) return;

    Object.keys(this.dependencies).forEach(sectionId => {
      this.checkSectionDependencies(sectionId);
    });

    const dependencyChangeEvent = new CustomEvent('dependencyStatusChanged', {
      detail: { 
        timestamp: Date.now(),
        dependencies: this.dependencies
      }
    });
    document.dispatchEvent(dependencyChangeEvent);
  }

  checkSectionDependencies(sectionId) {
    const prerequisites = this.dependencies[sectionId];
    const allPrerequisitesMet = prerequisites.every(prereq => this.isPrerequisiteMet(prereq));
    
    this.updateSectionState(sectionId, allPrerequisitesMet, prerequisites);
  }

  isPrerequisiteMet(prerequisite) {
    switch (prerequisite) {
      case 'header':
        return this.isHeaderSelected();
      case 'size':
        return this.isSizeComplete();
      default:
        return false;
    }
  }

  isHeaderSelected() {
    const headerInput = document.querySelector('input[name="properties[Header]"]');
    return headerInput && headerInput.value && headerInput.value.trim() !== '';
  }

  isSizeComplete() {
    const widthInput = document.querySelector('input[name="properties[Panel Width (in)]"]');
    const lengthInput = document.querySelector('input[name="properties[Panel Length (in)]"]');
    
    const width = widthInput ? parseFloat(widthInput.value) || 0 : 0;
    const length = lengthInput ? parseFloat(lengthInput.value) || 0 : 0;
    
    return width >= 12 && length >= 12;
  }

  updateSectionState(sectionId, isAccessible, prerequisites) {
    const sectionButton = document.querySelector(`[data-option-group="${sectionId}"]`);
    const accordion = sectionButton?.closest('.product-accordion');
    
    if (!accordion) return;

    if (isAccessible) {
      this.enableSection(accordion, sectionId);
    } else {
      this.disableSection(accordion, sectionId, prerequisites);
    }
  }

  enableSection(accordion, sectionId) {
    const button = accordion.querySelector('.acc-title');
    const content = accordion.querySelector('.acc-content');
    
    button.classList.remove('disabled');
    button.style.opacity = '';
    button.style.pointerEvents = '';
    
    this.hideDependencyMessage(accordion);
    
    this.showNormalContent(accordion, sectionId);
  }

  disableSection(accordion, sectionId, prerequisites) {
    const button = accordion.querySelector('.acc-title');
    const content = accordion.querySelector('.acc-content');
    
    button.classList.add('disabled');
    button.style.opacity = '0.6';
    button.style.pointerEvents = 'none';
    
    if (button.classList.contains('active')) {
      content.style.maxHeight = '0px';
      button.classList.remove('active');
      content.classList.remove('active');
      content.classList.remove('show-dropdown');
      
      setTimeout(() => {
        if (!content.classList.contains('active')) {
          content.style.display = 'none';
        }
      }, 400);
    }
    
    this.showDependencyMessage(accordion, sectionId, prerequisites);
    
    this.hideNormalContent(accordion, sectionId);
  }

  showDependencyMessage(accordion, sectionId, prerequisites) {
    let messageContainer = accordion.querySelector('.dependency-message');
    
    if (!messageContainer) {
      messageContainer = document.createElement('div');
      messageContainer.className = 'dependency-message';
      messageContainer.style.cssText = `
        padding: 16px;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        margin: 12px 0;
        text-align: center;
        color: #6c757d;
      `;
      
      const content = accordion.querySelector('.acc-content');
      if (content) {
        content.insertBefore(messageContainer, content.firstChild);
      }
    }

    const message = this.getDependencyMessage(sectionId, prerequisites);
    messageContainer.innerHTML = message;
    messageContainer.style.display = 'block';
  }

  hideDependencyMessage(accordion) {
    const messageContainer = accordion.querySelector('.dependency-message');
    if (messageContainer) {
      messageContainer.style.display = 'none';
    }
  }

  getDependencyMessage(sectionId, prerequisites) {
    const missingPrereqs = prerequisites.filter(prereq => !this.isPrerequisiteMet(prereq));
    
    const prereqMessages = {
      'header': 'Header type',
      'size': 'Curtain dimensions (width and length)'
    };

    const missing = missingPrereqs.map(prereq => prereqMessages[prereq]).join(' and ');

    let sizeMessage = '';
    if (missingPrereqs.includes('size')) {
      const widthInput = document.querySelector('input[name="properties[Panel Width (in)]"]');
      const lengthInput = document.querySelector('input[name="properties[Panel Length (in)]"]');
      
      const width = widthInput ? parseFloat(widthInput.value) || 0 : 0;
      const length = lengthInput ? parseFloat(lengthInput.value) || 0 : 0;
      
      if (width > 0 && width < 12) {
        sizeMessage = 'Width must be at least <strong>12 inches</strong>';
      } else if (length > 0 && length < 12) {
        sizeMessage = 'Length must be at least <strong>12 inches</strong>';
      } else if (width > 0 && length === 0) {
        sizeMessage = 'Please enter the <strong>Length</strong> (minimum 12 inches) to complete curtain dimensions';
      } else if (length > 0 && width === 0) {
        sizeMessage = 'Please enter the <strong>Width</strong> (minimum 12 inches) to complete curtain dimensions';
      } else if ((width > 0 && width < 12) || (length > 0 && length < 12)) {
        sizeMessage = 'Both width and length must be at least <strong>12 inches</strong>';
      } else {
        sizeMessage = 'Please enter both <strong>Width and Length</strong> (minimum 12x12 inches) for curtain dimensions';
      }
    }

    const sectionMessages = {
      'size': `Please select a <strong>Header type</strong> first before entering curtain dimensions.`,
      'lining': missingPrereqs.includes('size') && sizeMessage ? 
        `Please complete Header selection and ${sizeMessage.toLowerCase()} first before choosing lining options.` :
        `Please complete <strong>${missing}</strong> selection first before choosing lining options.`,
      'heat-setting': missingPrereqs.includes('size') && sizeMessage ? 
        `Please complete Header selection and ${sizeMessage.toLowerCase()} first before choosing heat setting options.` :
        `Please complete <strong>${missing}</strong> selection first before choosing heat setting options.`,
      'ties': missingPrereqs.includes('size') && sizeMessage ? 
        `Please complete Header selection and ${sizeMessage.toLowerCase()} first before choosing tie back options.` :
        `Please complete <strong>${missing}</strong> selection first before choosing tie back options.`
    };

    return `
      <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
        <svg width="20" height="20" fill="#6c757d" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
        <span>${sectionMessages[sectionId] || 'Please complete previous selections first.'}</span>
      </div>
    `;
  }

  showNormalContent(accordion, sectionId) {
    const normalContent = accordion.querySelectorAll('.image-selector-container, .dimensions-section');
    normalContent.forEach(element => {
      element.style.display = '';
    });

    const loadingElements = accordion.querySelectorAll('.header-loading, .lining-loading, .heat-setting-loading, .tie-backs-loading');
    loadingElements.forEach(element => {
      const isHeaderLoading = element.classList.contains('header-loading');
      const isLiningLoading = element.classList.contains('lining-loading');
      const isHeatSettingLoading = element.classList.contains('heat-setting-loading');
      const isTieBacksLoading = element.classList.contains('tie-backs-loading');
      
      if (isHeaderLoading) {
        const headerContainer = accordion.querySelector('.image-selector-container[data-property-name="Header"]');
        const hasContent = headerContainer && headerContainer.children.length > 0 && headerContainer.style.display !== 'none';
        if (!hasContent) {
          element.style.display = 'block';
        }
      } else if (isLiningLoading) {
        const liningContainer = accordion.querySelector('.image-selector-container[data-property-name="Lining"]');
        const hasContent = liningContainer && liningContainer.children.length > 0 && liningContainer.style.display !== 'none';
        if (!hasContent) {
          element.style.display = 'block';
        }
      } else if (isHeatSettingLoading) {
        const heatSettingContainer = accordion.querySelector('.image-selector-container[data-property-name="Heat Setting"]');
        const hasContent = heatSettingContainer && heatSettingContainer.children.length > 0 && heatSettingContainer.style.display !== 'none';
        if (!hasContent) {
          element.style.display = 'block';
        }
      } else if (isTieBacksLoading) {
        const tieBacksContainer = accordion.querySelector('.image-selector-container[data-property-name="Tie Backs"]');
        const hasContent = tieBacksContainer && tieBacksContainer.children.length > 0 && tieBacksContainer.style.display !== 'none';
        if (!hasContent) {
          element.style.display = 'block';
        }
      }
    });
  }

  hideNormalContent(accordion, sectionId) {
    const normalContent = accordion.querySelectorAll('.image-selector-container, .dimensions-section');
    normalContent.forEach(element => {
      element.style.display = 'none';
    });

    const loadingElements = accordion.querySelectorAll('.header-loading, .lining-loading, .heat-setting-loading, .tie-backs-loading');
    loadingElements.forEach(element => {
      element.style.display = 'none';
    });
  }

  getMissingPrerequisites(sectionId) {
    const prerequisites = this.dependencies[sectionId] || [];
    return prerequisites.filter(prereq => !this.isPrerequisiteMet(prereq));
  }

  isSectionAccessible(sectionId) {
    const prerequisites = this.dependencies[sectionId] || [];
    return prerequisites.every(prereq => this.isPrerequisiteMet(prereq));
  }
}

// Product Form Validator - Intercepts form submission and validates required fields
class ProductFormValidator {
  constructor(customProductOptions) {
    this.customOptions = customProductOptions;
    this.productFormId = customProductOptions.productFormId;
    this.isInitialized = false;
    this.validationRules = {
      header: {
        required: true,
        selector: 'input[name="properties[Header]"]',
        message: 'Please select a header type'
      },
      size: {
        required: true,
        validator: () => this.validateSize(),
        message: 'Please enter a valid curtain dimensions (width and length must be at least 12 inches)'
      },
      lining: {
        required: true,
        selector: 'input[name="properties[Lining]"]',
        message: 'Please select a lining type'
      }
    };
  }

  initialize() {
    if (this.isInitialized) return;

    try {
      this.setupFormInterception();
      this.setupRealTimeValidation(); 
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing product form validator:', error);
    }
  }

  setupFormInterception() {
    const productForm = document.getElementById(this.productFormId);
    if (!productForm) {
      console.warn(`Product form with ID ${this.productFormId} not found`);
      return;
    }

    this.cartManager = new CartManager(this.productFormId, this.formValidator);

    productForm.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.handleAddToCart();
      return false;
    }, true); 

    const addToCartButton = productForm.querySelector('.single-add-to-cart-button, button[type="submit"], input[type="submit"]');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleAddToCart();
        return false;
      }, true); 
    }

    const productFormComponent = document.querySelector('product-form');
    if (productFormComponent) {
      productFormComponent.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleAddToCart();
        return false;
      }, true); 
    }

    document.addEventListener('submit', (event) => {  
      if (event.target.id === this.productFormId || 
          event.target.closest(`#${this.productFormId}`) ||
          event.target.querySelector(`#${this.productFormId}`)) {
        
        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleAddToCart();
        return false;
      }
    }, true); 

    document.addEventListener('click', (event) => {
      const target = event.target;
      
      if ((target.matches('.single-add-to-cart-button, button[name="add"], button[type="submit"]') ||
           target.closest('.single-add-to-cart-button, button[name="add"], button[type="submit"]')) &&
          (target.closest(`#${this.productFormId}`) || 
           target.form?.id === this.productFormId)) {
        
        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleAddToCart();
        return false;
      }
    }, true); 
  }

  async handleAddToCart() {
    try {
      const isValid = this.validateAllFields();
      
      if (!isValid) {
        this.showValidationErrors();
        return;
      }

      await this.cartManager.addToCart();
      
    } catch (error) {
      console.error('Error in handleAddToCart:', error);
      this.cartManager.handleAddToCartError(error.message || 'Add to cart error, please try again.');
    }
  }

  handleFormSubmission(event) {
    const isValid = this.validateAllFields();
    
    if (!isValid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showValidationErrors();
      return false;
    }

    return true;
  }

  validateAllFields() {
    const errors = [];
    
    for (const [fieldName, rule] of Object.entries(this.validationRules)) {
      if (!rule.required) continue;

      let isValid = false;

      if (rule.validator) {
        isValid = rule.validator();
      } else if (rule.selector) {
        const element = document.querySelector(rule.selector);
        isValid = element && element.value && element.value.trim() !== '';
      }

      if (!isValid) {
        errors.push({
          field: fieldName,
          message: rule.message,
          selector: rule.selector
        });
      }
    }

    if (this.customOptions.dependencyManager && this.customOptions.dependencyManager.isInitialized) {
      const dependencyErrors = this.validateDependencies();
      errors.push(...dependencyErrors);
    }

    return errors.length === 0;
  }

  validateSize() {
    const container = document.querySelector('.custom-product-options-container');
    if (!container) return false;

    const widthInput = container.querySelector('input[name="properties[Panel Width (in)]"]');
    const lengthInput = container.querySelector('input[name="properties[Panel Length (in)]"]');

    if (!widthInput || !lengthInput) return false;

    const width = parseFloat(widthInput.value) || 0;
    const length = parseFloat(lengthInput.value) || 0;

    return width >= 12 && length >= 12;
  }

  validateDependencies() {
    const errors = [];
    const dependencyManager = this.customOptions.dependencyManager;

    const sectionsToCheck = ['size', 'lining', 'heat-setting', 'ties'];
    
    sectionsToCheck.forEach(sectionId => {
      if (!dependencyManager.isSectionAccessible(sectionId)) {
        const missingPrereqs = dependencyManager.getMissingPrerequisites(sectionId);
        if (missingPrereqs.length > 0) {
          errors.push({
            field: sectionId,
            message: `${this.getSectionDisplayName(sectionId)} must be completed first: ${missingPrereqs.map(prereq => this.getPrerequisiteDisplayName(prereq)).join('、')}`,
            type: 'dependency'
          });
        }
      }
    });

    return errors;
  }

  getSectionDisplayName(sectionId) {
    const displayNames = {
      'size': 'Size setting',
      'lining': 'Lining selection',
      'heat-setting': 'Heat setting',
      'ties': 'Tie back selection'
    };
    return displayNames[sectionId] || sectionId;
  }

  getPrerequisiteDisplayName(prereq) {
    const displayNames = {
      'header': 'Header type',
      'size': 'Curtain dimensions'
    };
    return displayNames[prereq] || prereq;
  }

  showValidationErrors() {
    this.clearValidationErrors();

    const errors = this.getValidationErrors();
    
    if (errors.length === 0) return;

    this.displayProductFormErrors(errors);

    this.scrollToFirstError(errors);
  }

  displayProductFormErrors(errors) {
    const customConfigErrors = errors.filter(error => 
      ['header', 'size', 'lining', 'heat-setting', 'ties'].includes(error.field)
    );
    const otherErrors = errors.filter(error => 
      !['header', 'size', 'lining', 'heat-setting', 'ties'].includes(error.field)
    );

    if (customConfigErrors.length > 0) {
      this.showCustomValidationErrors(customConfigErrors);
    }

    if (otherErrors.length > 0) {
      const errorMessages = otherErrors.map(error => error.message).join(', ');
      if (this.customOptions && this.customOptions.cartManager) {
        this.customOptions.cartManager.showProductFormError(errorMessages);
      }
    }
  }

  showCustomValidationErrors(errors) {
    const productForm = document.getElementById(this.productFormId);
    if (!productForm) return;

    const existingErrorSummary = document.querySelector('.form-validation-error-summary');
    if (existingErrorSummary) {
      existingErrorSummary.remove();
    }

    const errorSummary = document.createElement('div');
    errorSummary.className = 'form-validation-error-summary';
    errorSummary.style.cssText = `
      background: #fee;
      border: 1px solid #fcc;
      border-radius: 6px;
      padding: 16px;
      margin-bottom: 16px;
      color: #dc2626;
    `;

    const errorTitle = document.createElement('div');
    errorTitle.style.cssText = `
      font-weight: 600;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;
    errorTitle.innerHTML = `
      <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
      </svg>
      Please complete the following required selections:
    `;

    const errorList = document.createElement('ul');
    errorList.style.cssText = `
      margin: 0;
      padding: 0 0 0 20px;
      list-style-type: disc;
    `;

    errors.forEach(error => {
      const listItem = document.createElement('li');
      listItem.textContent = error.message;
      listItem.style.marginBottom = '4px';
      errorList.appendChild(listItem);
    });

    errorSummary.appendChild(errorTitle);
    errorSummary.appendChild(errorList);

    productForm.parentNode.insertBefore(errorSummary, productForm);

    this.showFieldErrors(errors);
  }

  showProductFormErrorDirect(message) {
    try {
      const productFormElement = this.getProductFormElement();
      
      if (productFormElement && typeof productFormElement.handleErrorMessage === 'function') {
        productFormElement.handleErrorMessage(message);
      }
    } catch (error) {
      console.error('Error showing validation error:', error);
    }
  }

  getProductFormElement() {
    const productForm = document.getElementById(this.productFormId);
    return productForm?.closest('product-form') || productForm;
  }

  getValidationErrors() {
    const errors = [];
    
    for (const [fieldName, rule] of Object.entries(this.validationRules)) {
      if (!rule.required) continue;

      let isValid = false;

      if (rule.validator) {
        isValid = rule.validator();
      } else if (rule.selector) {
        const element = document.querySelector(rule.selector);
        isValid = element && element.value && element.value.trim() !== '';
      }

      if (!isValid) {
        errors.push({
          field: fieldName,
          message: rule.message,
          selector: rule.selector
        });
      }
    }

    if (this.customOptions.dependencyManager && this.customOptions.dependencyManager.isInitialized) {
      const dependencyErrors = this.validateDependencies();
      errors.push(...dependencyErrors);
    }

    return errors;
  }



  findSectionForField(fieldName) {
    const sectionMap = {
      'header': '[data-option-group="header"]',
      'size': '[data-option-group="size"]',
      'lining': '[data-option-group="lining"]'
    };

    const selector = sectionMap[fieldName];
    if (selector) {
      const button = document.querySelector(selector);
      return button?.closest('.product-accordion');
    }

    return null;
  }

  highlightErrorSection(section, message) {
    const button = section.querySelector('.acc-title');
    if (button) {
      button.style.borderColor = '#dc2626';
      button.style.backgroundColor = '#fef2f2';
      
      let errorIndicator = button.querySelector('.validation-error-indicator');
      if (!errorIndicator) {
        errorIndicator = document.createElement('span');
        errorIndicator.className = 'validation-error-indicator';
        errorIndicator.style.cssText = `
          color: #dc2626;
          font-size: 12px;
          margin-left: 8px;
        `;
        button.querySelector('.left-side').appendChild(errorIndicator);
      }
      errorIndicator.textContent = '⚠️';
    }
  }

  showFieldErrors(errors) {
    errors.forEach(error => {
      const section = this.findSectionForField(error.field);
      if (section) {
        this.highlightErrorSection(section, error.message);
      }
    });
  }

  scrollToFirstError(errors) {
    if (errors.length === 0) return;

    const productForm = document.getElementById(this.productFormId);
    const errorWrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    
    if (errorWrapper && !errorWrapper.hasAttribute('hidden')) {
      errorWrapper.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    } else {
      const errorSummary = document.querySelector('.form-validation-error-summary');
      if (errorSummary) {
        errorSummary.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }

  clearValidationErrors() {
    const existingErrors = document.querySelectorAll('.form-validation-error-summary');
    existingErrors.forEach(error => error.remove());

    this.clearFieldHighlights();

  }

  hideProductFormErrorDirect() {
    try {
      const productFormElement = this.getProductFormElement();
      
      if (productFormElement && typeof productFormElement.handleErrorMessage === 'function') {
        productFormElement.handleErrorMessage(false);
      }
    } catch (error) {
      console.error('Error hiding product form error:', error);
    }
  }

  triggerValidation() {
    return this.validateAllFields();
  }

  getValidationStatus() {
    const errors = this.getValidationErrors();
    return {
      isValid: errors.length === 0,
      errors: errors,
      errorCount: errors.length
    };
  }
  
  setupRealTimeValidation() {
    
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="properties[Header]"]')) {
        this.checkAndClearErrors();
      }
    });

    
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="properties[Lining]"]')) {
        this.checkAndClearErrors();
      }
    });

    
    const sizeInputs = document.querySelectorAll('input[name="properties[Panel Width (in)]"], input[name="properties[Panel Length (in)]"]');
    sizeInputs.forEach(input => {
      input.addEventListener('input', this.debounce(() => {
        this.checkAndClearErrors();
      }, 500));
      
      input.addEventListener('change', () => {
        this.checkAndClearErrors();
      });
    });

    
    document.addEventListener('configurationChanged', () => {
      this.checkAndClearErrors();
    });

    
    document.addEventListener('dependencyStatusChanged', () => {
      this.checkAndClearErrors();
    });

    
    const accordionButtons = document.querySelectorAll('.custom-product-options-container .acc-title');
    accordionButtons.forEach(button => {
      button.addEventListener('click', () => {
        
        setTimeout(() => {
          this.checkAndClearErrors();
        }, 100);
      });
    });

  }

  
  checkAndClearErrors() {
    const existingErrors = document.querySelector('.form-validation-error-summary');
    if (!existingErrors) return;

    const customFieldsValid = this.validateCustomFields();
    
    if (customFieldsValid) {
      this.clearValidationErrors();
      this.clearFieldHighlights();
    } else {
      this.updateCustomValidationErrors();
    }
    }

  validateCustomFields() {
    const customFieldErrors = [];
    
    const customValidationRules = {
      header: this.validationRules.header,
      size: this.validationRules.size,
      lining: this.validationRules.lining
    };
    
    for (const [fieldName, rule] of Object.entries(customValidationRules)) {
      if (!rule.required) continue;

      let isValid = false;

      if (rule.validator) {
        isValid = rule.validator();
      } else if (rule.selector) {
        const element = document.querySelector(rule.selector);
        isValid = element && element.value && element.value.trim() !== '';
      }

      if (!isValid) {
        customFieldErrors.push({
          field: fieldName,
          message: rule.message,
          selector: rule.selector
        });
      }
    }

    if (this.customOptions.dependencyManager && this.customOptions.dependencyManager.isInitialized) {
      const dependencyErrors = this.validateDependencies();
      customFieldErrors.push(...dependencyErrors);
    }

    return customFieldErrors.length === 0;
  }

  updateCustomValidationErrors() {
    const customFieldErrors = [];
    
    const customValidationRules = {
      header: this.validationRules.header,
      size: this.validationRules.size,
      lining: this.validationRules.lining
    };
    
    for (const [fieldName, rule] of Object.entries(customValidationRules)) {
      if (!rule.required) continue;

      let isValid = false;

      if (rule.validator) {
        isValid = rule.validator();
      } else if (rule.selector) {
        const element = document.querySelector(rule.selector);
        isValid = element && element.value && element.value.trim() !== '';
      }

      if (!isValid) {
        customFieldErrors.push({
          field: fieldName,
          message: rule.message,
          selector: rule.selector
        });
      }
    }

    if (this.customOptions.dependencyManager && this.customOptions.dependencyManager.isInitialized) {
      const dependencyErrors = this.validateDependencies();
      customFieldErrors.push(...dependencyErrors);
    }
    
    if (customFieldErrors.length === 0) {
      this.clearValidationErrors();
      return;
    }

    const existingErrorSummary = document.querySelector('.form-validation-error-summary');
    if (existingErrorSummary) {
      const errorList = existingErrorSummary.querySelector('ul');
      if (errorList) {
        errorList.innerHTML = '';
        customFieldErrors.forEach(error => {
          const listItem = document.createElement('li');
          listItem.textContent = error.message;
          listItem.style.marginBottom = '4px';
          errorList.appendChild(listItem);
        });
      }
    }

    this.clearFieldHighlights();
    this.showFieldErrors(customFieldErrors);
  }

  clearFieldHighlights() {
    const accordions = document.querySelectorAll('.product-accordion .acc-title');
    accordions.forEach(button => {
      button.style.borderColor = '';
      button.style.backgroundColor = '';
      
      const errorIndicator = button.querySelector('.validation-error-indicator');
      if (errorIndicator) {
        errorIndicator.remove();
      }
    });
  }

  
  updateValidationErrors() {
    const errors = this.getValidationErrors();
    
    if (errors.length === 0) {
      this.clearValidationErrors();
      return;
    }

    const existingErrorSummary = document.querySelector('.form-validation-error-summary');
    if (existingErrorSummary) {
      
      const errorList = existingErrorSummary.querySelector('ul');
      if (errorList) {
        errorList.innerHTML = '';
        errors.forEach(error => {
          const listItem = document.createElement('li');
          listItem.textContent = error.message;
          listItem.style.marginBottom = '4px';
          errorList.appendChild(listItem);
        });
      }
    }

    this.clearFieldHighlights();
    this.showFieldErrors(errors);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

class CartManager {
  constructor(productFormId, formValidator) {
    this.productFormId = productFormId;
    this.formValidator = formValidator;
    this.isLoading = false;
    this.customSuccessHandlers = [];
    this.customErrorHandlers = [];
    this.options = {
      autoOpenCartDrawer: true,
      updateCartDrawerContent: true,
      showFallbackMessage: true,
      maxCartItems: 20  // Maximum number of line items allowed in cart
    };
  }

  addSuccessHandler(handler) {
    if (typeof handler === 'function') {
      this.customSuccessHandlers.push(handler);
    }
  }

  addErrorHandler(handler) {
    if (typeof handler === 'function') {
      this.customErrorHandlers.push(handler);
    }
  }

  setOptions(newOptions) {
    this.options = { ...this.options, ...newOptions };
  }

  /**
   * Set cart item limit
   * @param {number} maxItems - Maximum number of line items (must be a positive number)
   * 
   * Usage example:
   * window.curtainProductOptions.cartManager.setMaxCartItems(30); // Set limit to 30 line items
   */
  setMaxCartItems(maxItems) {
    if (typeof maxItems === 'number' && maxItems > 0) {
      this.options.maxCartItems = maxItems;
      console.log(`Cart item limit set to: ${maxItems}`);
    } else {
      console.warn('Invalid maxItems value, must be a positive number');
    }
  }

  async addToCart() {
    if (this.isLoading) {
      return;
    }

    try {
      this.isLoading = true;
      this.showLoading();
      
      this.hideProductFormError();

      // Check cart item count before adding
      const cartCheckResult = await this.checkCartItemCount();
      if (!cartCheckResult.canAdd) {
        this.handleAddToCartError(cartCheckResult.message);
        return;
      }

      const cartData = this.collectCartData();
      

      const response = await this.callCartAddAPI(cartData);
      
      if (response.success) {
        await this.handleAddToCartSuccess(response.data);
      } else {
        this.handleAddToCartError(response.error);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleAddToCartError(error.message || 'Error adding to cart');
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  collectCartData() {
    const productForm = document.getElementById(this.productFormId);
    if (!productForm) {
      throw new Error('Product form not found');
    }

    const variantSelector = productForm.querySelector('input[name="id"], select[name="id"]');
    const variantId = variantSelector ? variantSelector.value : null;
    
    if (!variantId) {
      throw new Error('No variant selected');
    }

    const quantityInput = productForm.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    const properties = {};
    const customOptionsContainer = document.querySelector('.custom-product-options-container');
    
    if (customOptionsContainer) {
      const propertyInputs = customOptionsContainer.querySelectorAll('input[name^="properties["]');
      
      propertyInputs.forEach(input => {
        if (input.value && input.value.trim() !== '') {
          const propertyName = input.name.match(/properties\[([^\]]+)\]/)[1];
          properties[propertyName] = input.value.trim();
        }
      });
    }
    properties._Currency = Shopify.currency.active;

    const targetPrice = this.calculateTargetPrice();
    
    const groupID = `curtain_combo_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    

    const adjustmentProductVariantId = '46727143883008';

    const items = [
      {
        id: adjustmentProductVariantId,
        quantity: quantity,
        properties: { 
          __sectionly_custom_option: JSON.stringify({
            group: groupID,
            isParent: true,
            price: targetPrice.toString()
          })
        }
      },
      {
        id: variantId,
        quantity: quantity,
        properties: {   
          __sectionly_custom_option: JSON.stringify({
            group: groupID
          }),
          __sectionly_custom_properties: JSON.stringify({
            _sectionly_groupId: groupID,
            ...properties,
          }),
          ...properties
        }
      }
    ];

    return {
      items: items
    };
  }

  calculateTargetPrice() {
    try {
      if (window.curtainProductOptions && window.curtainProductOptions.pricePreview) {
        const pricePreview = window.curtainProductOptions.pricePreview;
        
        if (pricePreview.calculator && pricePreview.isInitialized) {
          const configData = this.collectCurrentConfiguration();
          
          if (pricePreview.hasMinimumRequiredData(configData)) {
            const calculationResult = pricePreview.calculator.calculateTotal(configData);
            
            if (calculationResult.success) {
              // 计算器返回的是包含数量的总价，我们需要除以数量得到单价
              const totalPrice = calculationResult.total;
              const quantity = configData.curtainQuantity || 1;
              const unitPrice = totalPrice / quantity;
              return parseFloat(unitPrice.toFixed(2));
            }
          }
        }
      }

      // Fallback calculation
      const configData = this.collectCurrentConfiguration();
      const basePrice = this.getBaseSingleProductPrice(configData);
      
      return parseFloat(basePrice.toFixed(2));
      
    } catch (error) {
      console.error('Error calculating target price:', error);
      
      const defaultPrice = 100.00;
      return defaultPrice;
    }
  }

  collectCurrentConfiguration() {
    const container = document.querySelector('.custom-product-options-container');
    if (!container) return {};

    const widthInput = container.querySelector('input[name="properties[Panel Width (in)]"]');
    const lengthInput = container.querySelector('input[name="properties[Panel Length (in)]"]');
    const headerInput = container.querySelector('input[name="properties[Header]"]');
    const liningInput = container.querySelector('input[name="properties[_Lining ID]"]');
    const heatSettingInput = container.querySelector('input[name="properties[Heat Setting]"]');
    const tieBacksQuantityInput = container.querySelector('input[name="properties[_Tie Backs Quantity]"]');

    const fabricId = window.currentProductFabric?.fabricId || 'luxe_velvet';
    
    const productForm = document.getElementById(this.productFormId);
    const quantityInput = productForm?.querySelector('input[name="quantity"]');
    const curtainQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    return {
      widthInches: widthInput ? parseFloat(widthInput.value) || 0 : 0,
      lengthInches: lengthInput ? parseFloat(lengthInput.value) || 0 : 0,
      headerType: this.getHeaderTypeFromValue(headerInput?.value),
      fabricId: fabricId,
      liningId: liningInput?.value || 'unlined',
      heatSetting: heatSettingInput?.value === 'Yes',
      tieCount: tieBacksQuantityInput ? parseInt(tieBacksQuantityInput.value) || 0 : 0,
      curtainQuantity: curtainQuantity,
      
      selectedHeader: headerInput?.value,
      selectedLining: container.querySelector('input[name="properties[Lining]"]')?.value,
      projectName: container.querySelector('input[name="properties[Project Name]"]')?.value
    };
  }

  getHeaderTypeFromValue(headerValue) {
    if (!headerValue) return null;
    
    try {
      if (window.curtainConfigLoader) {
        const headerTypes = window.curtainConfigLoader.getEnabledHeaderTypes();
        
        for (const [id, header] of Object.entries(headerTypes)) {
          if (header.name === headerValue) {
            return id;
          }
        }
      }
    } catch (error) {
      console.error('Error getting header type:', error);
    }
    
    return null;
  }

  getBaseProductPrice(configData) {
    if (window.currentProductPricing && window.currentProductPricing.basePriceDecimal) {
      const basePrice = parseFloat(window.currentProductPricing.basePriceDecimal);
      const quantity = configData.curtainQuantity || 1;
      return basePrice * quantity;
    }

    try {
      if (window.curtainProductOptions && window.curtainProductOptions.pricePreview) {
        const pricePreview = window.curtainProductOptions.pricePreview;
        if (pricePreview.calculator && pricePreview.calculator.getBaseProductPriceInfo) {
          const basePriceInfo = pricePreview.calculator.getBaseProductPriceInfo();
          if (basePriceInfo && basePriceInfo.baseProductPrice) {
            const quantity = configData.curtainQuantity || 1;
            return basePriceInfo.baseProductPrice * quantity;
          }
        }
      }
    } catch (error) {
      console.error('Error getting base product price:', error);
    }

    return 0;
  }

  getBaseSingleProductPrice(configData) {
    if (window.currentProductPricing && window.currentProductPricing.basePriceDecimal) {
      const basePrice = parseFloat(window.currentProductPricing.basePriceDecimal);
      return basePrice;
    }

    try {
      if (window.curtainProductOptions && window.curtainProductOptions.pricePreview) {
        const pricePreview = window.curtainProductOptions.pricePreview;
        if (pricePreview.calculator && pricePreview.calculator.getBaseProductPriceInfo) {
          const basePriceInfo = pricePreview.calculator.getBaseProductPriceInfo();
          if (basePriceInfo && basePriceInfo.baseProductPrice) {
            return basePriceInfo.baseProductPrice;
          }
        }
      }
    } catch (error) {
      console.error('Error getting base single product price:', error);
    }

    return 0;
  }

  /**
   * Check if cart item count exceeds the limit
   * Counts each cart line item as a separate item
   * @returns {Promise<{canAdd: boolean, message: string}>} Check result
   */
  async checkCartItemCount() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        // If we can't get cart info, allow adding (fail gracefully)
        return {
          canAdd: true,
          message: ''
        };
      }

      const cart = await response.json();
      
      // Count cart items directly - each item in cart.items represents a separate product entry
      const currentItemCount = cart.items ? cart.items.length : 0;
      const totalQuantity = cart.item_count || 0;

      console.log('Cart analysis:', {
        itemCount: currentItemCount,
        totalQuantity: totalQuantity,
        limit: this.options.maxCartItems
      });

      if (currentItemCount >= this.options.maxCartItems) {
        console.log('Cart item limit reached, blocking add to cart');
        return {
          canAdd: false,
          message: `Cart item limit reached. Please clear your cart or remove some items before adding new products. You can click the cart icon to manage items in your cart.`
        };
      }

      return {
        canAdd: true,
        message: ''
      };

    } catch (error) {
      console.error('Error checking cart item count:', error);
      // If there's an error checking cart, allow adding (fail gracefully)
      return {
        canAdd: true,
        message: ''
      };
    }
  }

  async callCartAddAPI(cartData) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      });

      const responseData = await response.json();

      if (response.ok) {
        return {
          success: true,
          data: responseData
        };
      } else {
        return {
          success: false,
          error: responseData.message || responseData.description || 'Unknown error'
        };
      }

    } catch (error) {
      console.error('Cart API error:', error);
      return {
        success: false,
        error: error.message || 'Network error'
      };
    }
  }

  async handleAddToCartSuccess(cartData) {

    if (cartData.items && Array.isArray(cartData.items)) {
      cartData.items.forEach((item, index) => {
        const productType = item.properties && item.properties['_Product Type'] ? 
          item.properties['_Product Type'] : 'Unknown';
        
        if (productType === 'Price Adjustment') {
          const targetPrice = item.properties && item.properties['_Target Price'];
          if (targetPrice) {
            console.log(`  → Target price: $${targetPrice}`);
          }
        }
      });
    }

    const addToCartEvent = new CustomEvent('productAddedToCart', {
      detail: {
        cartData: cartData,
        timestamp: Date.now(),
        hasAdjustmentProduct: true
      }
    });
    document.dispatchEvent(addToCartEvent);

    await this.handleBundledProducts(cartData);

    if (this.options.updateCartDrawerContent) {
      await this.updateCartDrawer();
    } else {
      await this.updateCartCount();
    }

    if (this.options.autoOpenCartDrawer) {
      this.openCartDrawer();
    }

    for (const handler of this.customSuccessHandlers) {
      try {
        await handler(cartData);
      } catch (error) {
        console.error('Error in custom success handler:', error);
      }
    }
  }

  openCartDrawer() {
    try {
      const cartDrawer = document.getElementById('Cart-Drawer');
      
      if (cartDrawer) {
        cartDrawer.classList.add('active');
        
        const recommendations = cartDrawer.querySelector('.product-recommendations--full');
        if (recommendations) {
          recommendations.classList.add('active');
        }
        
        const cartDrawerOpenEvent = new CustomEvent('cart-drawer:open');
        document.dispatchEvent(cartDrawerOpenEvent);
        
      } else {
        if (this.options.showFallbackMessage) {
          this.showSuccess('Product added to cart successfully!');
          setTimeout(() => this.hideMessages(), 3000);
        }
      }
          } catch (error) {
        console.error('Error opening cart drawer:', error);
        if (this.options.showFallbackMessage) {
          this.showSuccess('Product added to cart successfully!');
          setTimeout(() => this.hideMessages(), 3000);
        }
      }
  }

  async updateCartDrawer() {
    try {
      const response = await fetch(`${window.location.origin}?section_id=cart-drawer`);
      
      if (response.ok) {
        const html = await response.text();
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        const newCartDrawer = tempDiv.querySelector('#Cart-Drawer');
        const existingCartDrawer = document.getElementById('Cart-Drawer');
        
        if (newCartDrawer && existingCartDrawer) {
          const wasActive = existingCartDrawer.classList.contains('active');
          
          existingCartDrawer.innerHTML = newCartDrawer.innerHTML;
          
          if (wasActive) {
            existingCartDrawer.classList.add('active');
          }
          
          if (existingCartDrawer.notesToggle) {
            existingCartDrawer.notesToggle();
          }
          if (existingCartDrawer.removeProductEvent) {
            existingCartDrawer.removeProductEvent();
          }
          
        }
      }
      
      await this.updateCartCount();
      
    } catch (error) {
      console.error('Error updating cart drawer:', error);
      await this.updateCartCount();
    }
  }

  async handleBundledProducts(mainCartData) {
    try {
      const bundledItems = this.getBundledItems(mainCartData);
      
      if (bundledItems && bundledItems.length > 0) {
        
        let bundledSuccessCount = 0;
        for (const bundledItem of bundledItems) {
          const response = await this.callCartAddAPI({ items: [bundledItem] });
          if (response.success) {
            bundledSuccessCount++;
          }
        }
        
        if (bundledSuccessCount > 0) {

          const bundledEvent = new CustomEvent('bundledProductsAdded', {
            detail: {
              count: bundledSuccessCount,
              items: bundledItems,
              timestamp: Date.now()
            }
          });
          document.dispatchEvent(bundledEvent);
        }
      }
      
    } catch (error) {
      console.error('Error adding bundled products:', error);
    }
  }

  getBundledItems(mainCartData) {
    const bundledItems = [];
    
    try {
      if (mainCartData.items && mainCartData.items.length > 0) {
        const mainItem = mainCartData.items[0];
        const properties = mainItem.properties || {};
        
        if (properties['Heat Setting'] === 'Yes') {
          // bundledItems.push({
          //   id: 'CARE_KIT_VARIANT_ID',
          //   quantity: 1,
          //   properties: {
          //     '_Bundle Type': 'Heat Setting Care Kit',
          //     '_Added Automatically': 'true'
          //   }
          // });
        }
        
        const fabricId = window.currentProductFabric?.fabricId;
        if (fabricId === 'silk_fabric') {
          // bundledItems.push({
          //   id: 'SILK_CARE_VARIANT_ID',
          //   quantity: 1
          // });
        }
        
        const width = parseFloat(properties['Panel Width (in)']) || 0;
        const length = parseFloat(properties['Panel Length (in)']) || 0;
        
        if (width > 60 || length > 84) {
          // bundledItems.push({
          //   id: 'HEAVY_DUTY_HARDWARE_VARIANT_ID',
          //   quantity: Math.ceil(width / 60)
          // });
        }
      }
      
    } catch (error) {
      console.error('Error determining bundled items:', error);
    }
    
    return bundledItems;
  }

  handleAddToCartError(errorMessage) {
    console.error('Add to cart failed:', errorMessage);
    
    if (errorMessage.includes('46727143883008') || errorMessage.includes('adjustment')) {
      errorMessage = `Please add at least one product to the cart. ${errorMessage}`;
    }
    
    // Special handling for cart limit error
    if (errorMessage.includes('Cart item limit reached')) {
      // Show a more prominent error message for cart limit
      this.showCartLimitError(errorMessage);
    } else {
      this.showProductFormError(errorMessage);
    }

    for (const handler of this.customErrorHandlers) {
      try {
        handler(errorMessage, errorMessage);
      } catch (error) {
        console.error('Error in custom error handler:', error);
      }
    }
  }

  showCartLimitError(message) {
    try {
      // First show the regular product form error
      this.showProductFormError(message);
      
      // Then show a more prominent popup-style error
      this.showError(message);
      
      // Auto-hide the popup after 5 seconds
      setTimeout(() => {
        this.hideMessages();
      }, 5000);
      
    } catch (error) {
      console.error('Error showing cart limit error:', error);
      // Fallback to regular error display
      this.showProductFormError(message);
    }
  }

  showProductFormError(message) {
    try {
      const productFormElement = this.getProductFormElement();
      
      if (productFormElement && typeof productFormElement.handleErrorMessage === 'function') {
        productFormElement.handleErrorMessage(message);
        
        const errorWrapper = productFormElement.querySelector('.product-form__error-message-wrapper');
        if (errorWrapper) {
          errorWrapper.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }
    } catch (error) {
      console.error('Error showing product form error:', error);
    }
  }

  hideProductFormError() {
    try {
      const productFormElement = this.getProductFormElement();
      
      if (productFormElement && typeof productFormElement.handleErrorMessage === 'function') {
        productFormElement.handleErrorMessage(false);
      }
    } catch (error) {
      console.error('Error hiding product form error:', error);
    }
  }

  getProductFormElement() {
    const productForm = document.getElementById(this.productFormId);
    return productForm?.closest('product-form') || productForm;
  }



  async updateCartCount() {
    try { 
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('.thb-item-count, .cart-count, [data-cart-count]');
      cartCountElements.forEach(element => {
        if (cart.item_count > 99) {
          element.textContent = cart.item_count;
        } else {
          element.textContent = cart.item_count;
        }
      });

      const cartUpdateEvent = new CustomEvent('cartUpdated', {
        detail: { cart: cart }
      });
      document.dispatchEvent(cartUpdateEvent);
      
      
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  showLoading() {
    const addToCartButton = this.getAddToCartButton();
    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.classList.add('loading');
      
      const buttonText = addToCartButton.querySelector('.single-add-to-cart-button--text');
      const loadingOverlay = addToCartButton.querySelector('.loading-overlay');
      
      if (buttonText) {
        buttonText.style.opacity = '0';
      }
      if (loadingOverlay) {
        loadingOverlay.style.display = 'block';
      }
    }
  }

  hideLoading() {
    const addToCartButton = this.getAddToCartButton();
    if (addToCartButton) {
      addToCartButton.disabled = false;
      addToCartButton.classList.remove('loading');
      
      const buttonText = addToCartButton.querySelector('.single-add-to-cart-button--text');
      const loadingOverlay = addToCartButton.querySelector('.loading-overlay');
      
      if (buttonText) {
        buttonText.style.opacity = '';
      }
      if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
      }
    }
  }

  getAddToCartButton() {
    const productForm = document.getElementById(this.productFormId);
    return productForm ? productForm.querySelector('.single-add-to-cart-button, button[type="submit"]') : null;
  }

  showSuccess(message) {
    this.showMessage(message, 'success');
  }

  showError(message) {
    this.showMessage(message, 'error');
  }

  showMessage(message, type = 'info') {
    this.hideMessages();

    const messageElement = document.createElement('div');
    messageElement.className = `cart-message cart-message--${type}`;
    messageElement.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#d4edda' : '#f8d7da'};
      color: ${type === 'success' ? '#155724' : '#721c24'};
      border: 1px solid ${type === 'success' ? '#c3e6cb' : '#f5c6cb'};
      border-radius: 6px;
      padding: 16px 20px;
      max-width: 400px;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      font-size: 14px;
      line-height: 1.4;
    `;

    const messageIcon = type === 'success' ? '✓' : '⚠️';
    messageElement.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 8px;">
        <span style="font-size: 16px;">${messageIcon}</span>
        <div>
          <div style="font-weight: 600; margin-bottom: 4px;">
            ${type === 'success' ? 'Success' : 'Error'}
          </div>
          <div>${message}</div>
        </div>
      </div>
    `;

    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      background: none;
      border: none;
      font-size: 18px;
      cursor: pointer;
      color: inherit;
      opacity: 0.7;
    `;
    closeButton.addEventListener('click', () => this.hideMessages());
    messageElement.appendChild(closeButton);

    document.body.appendChild(messageElement);

    requestAnimationFrame(() => {
      messageElement.style.transform = 'translateX(0)';
      messageElement.style.opacity = '1';
    });

    messageElement.style.transform = 'translateX(100%)';
    messageElement.style.opacity = '0';
    messageElement.style.transition = 'all 0.3s ease';
  }

  hideMessages() {
    const messages = document.querySelectorAll('.cart-message');
    messages.forEach(message => {
      message.style.transform = 'translateX(100%)';
      message.style.opacity = '0';
      setTimeout(() => {
        if (message.parentNode) {
          message.parentNode.removeChild(message);
        }
      }, 300);
    });
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const container = document.querySelector('.custom-product-options-container[data-product-form-id]');
  
  const productFormId = container?.dataset.productFormId || '';
  
  if (!productFormId) {
    return;
  }
  
  const customProductOptions = new CustomProductOptions(productFormId);
  customProductOptions.initialize();
});

// Dynamic Heat Setting Selector
class DynamicHeatSettingSelector {
  constructor() {
    this.configLoader = null;
    this.isInitialized = false;
  }

  async initialize() {
    try {
      this.configLoader = await window.curtainConfigLoader.getInitializationPromise();
      
      this.generateHeatSettingOptions();
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing dynamic heat setting selector:', error);
      this.showErrorState();
    }
  }

  generateHeatSettingOptions() {
    const heatSettingButton = document.querySelector('[data-option-group="heat-setting"]');
    if (!heatSettingButton) {
      console.error('Heat setting button not found');
      return;
    }
    
    const heatSettingAccordion = heatSettingButton.closest('.product-accordion');
    if (!heatSettingAccordion) {
      console.error('Heat setting accordion not found');
      return;
    }
    
    const heatSettingContainer = heatSettingAccordion.querySelector('.image-selector-container');
    const loadingElement = heatSettingAccordion.querySelector('.heat-setting-loading');
    
    if (!heatSettingContainer) {
      console.error('Heat setting container not found');
      return;
    }

    try {
      // Get heat setting options from config
      const additionalServices = this.configLoader.getAdditionalServices();
      const heatSettingConfig = additionalServices?.heat_setting_options || additionalServices?.heat_setting;
      
      if (!heatSettingConfig) {
        console.error('Heat setting configuration not found');
        this.showErrorState();
        return;
      }

      // Generate heat setting options from config
      let heatSettingOptions = [];
      
      if (Array.isArray(heatSettingConfig)) {
        // If it's already an array of options
        heatSettingOptions = heatSettingConfig;
      } else {
        // Generate standard options using config data
        heatSettingOptions = [
          {
            id: 'no_heat_setting',
            name: 'No Heat Setting',
            value: 'no',
            image: heatSettingConfig.no_image || 'https://cdn.shopify.com/s/files/1/0569/8789/3266/files/no_heat_setting.jpg?v=1704067200',
            price: 0,
            enabled: true
          },
          {
            id: 'heat_setting_process',
            name: heatSettingConfig.name || 'Heat Setting Process',
            value: 'yes',
            image: heatSettingConfig.image || 'https://cdn.shopify.com/s/files/1/0569/8789/3266/files/heat_setting.jpg?v=1704067200',
            price: heatSettingConfig.price_per_meter || 5.00,
            enabled: heatSettingConfig.enabled !== false
          }
        ];
      }
      
      // Filter enabled options
      const enabledOptions = heatSettingOptions.filter(option => option.enabled !== false);
      
      enabledOptions.forEach(option => {
        const optionElement = this.createHeatSettingOption(option);
        heatSettingContainer.appendChild(optionElement);
      });

      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
      heatSettingContainer.style.display = 'flex';

      this.setupHeatSettingOptionHandlers();
      
      if (heatSettingAccordion && heatSettingAccordion.recalculateHeight) {
        heatSettingAccordion.recalculateHeight();
      }
      
    } catch (error) {
      console.error('Error generating heat setting options:', error);
      this.showErrorState();
    }
  }

  createHeatSettingOption(option) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'image-option';
    button.dataset.value = option.value;
    button.dataset.heatSettingId = option.id;
    button.dataset.price = option.price;

    // Calculate actual total price using existing price calculation logic
    const totalPrice = this.getHeatSettingTotalPrice(option.price);
    const priceDisplay = totalPrice > 0 ? `+${this.formatPrice(totalPrice * Shopify.currency.rate)}` : this.formatPrice(0);

    button.innerHTML = `
      <img
        src="${option.image}"
        alt="${option.name}"
        width="100"
        height="100"
      >
      <div class="option-details">
        <div class="option-name">${option.name}</div>
        <div class="option-price">（${priceDisplay}）</div>
      </div>
    `;

    return button;
  }

  getHeatSettingTotalPrice(pricePerMeter) {
    try {
      // If pricePerMeter is 0, this is "No Heat Setting" option
      if (pricePerMeter === 0) {
        return 0;
      }

      // Use existing price calculation logic from PricePreviewManager
      if (window.curtainProductOptions?.pricePreview?.calculator) {
        const pricePreview = window.curtainProductOptions.pricePreview;

        if (!pricePreview.isInitialized) {
          return 0;
        }

        // Get current configuration
        const configData = pricePreview.collectCurrentConfiguration();

        // Check if we have minimum required data
        if (!pricePreview.hasMinimumRequiredData(configData)) {
          return 0;
        }

        // Calculate material usage using existing calculator
        const headerConfig = pricePreview.calculator.configLoader.getHeaderType(configData.headerType);
        const materialUsage = pricePreview.calculator.calculateMaterialUsage(
          configData.widthInches,
          configData.lengthInches,
          headerConfig
        );

        // Calculate heat setting cost: meters × pricePerMeter × quantity
        const quantity = configData.curtainQuantity || 1;
        const heatSettingCost = materialUsage.meters * pricePerMeter * quantity;

        return parseFloat(heatSettingCost.toFixed(2));
      }

      return 0;
    } catch (error) {
      console.warn('Error calculating heat setting total price:', error);
      return 0;
    }
  }

  updateHeatSettingPriceDisplay() {
    try {
      const heatSettingContainer = document.querySelector('.image-selector-container[data-property-name="Heat Setting"]');
      if (!heatSettingContainer) return;

      const options = heatSettingContainer.querySelectorAll('.image-option');
      options.forEach((option) => {
        const pricePerMeter = (parseFloat(option.dataset.price) || 0) * Shopify.currency.rate;
        const totalPrice = this.getHeatSettingTotalPrice(pricePerMeter);
        const priceDisplay = totalPrice > 0 ? `+${this.formatPrice(totalPrice)}` : this.formatPrice(0);

        const priceElement = option.querySelector('.option-price');
        if (priceElement) {
          priceElement.innerHTML = `（${priceDisplay}）`;
        }
      });
    } catch (error) {
      console.warn('Error updating heat setting price display:', error);
    }
  }

  /**
   * Format price using calculator's currency formatting
   * @param {number} amount - Price amount
   * @returns {string} Formatted price string
   */
  formatPrice(amount) {
    try {
      // First try to use calculator's formatPrice method
      if (window.curtainProductOptions?.pricePreview?.calculator) {
        const calculator = window.curtainProductOptions.pricePreview.calculator;
        if (calculator.formatPrice) {
          return calculator.formatPrice(amount);
        }
      }

      // Fallback to PricePreviewManager's getFormattedPrice method
      if (window.curtainProductOptions?.pricePreview?.getFormattedPrice) {
        return window.curtainProductOptions.pricePreview.getFormattedPrice(amount);
      }

      // Final fallback
      return `${formatMoney(amount.toFixed(2))}`;
    } catch (error) {
      console.warn('Error formatting price:', error);
      return `${formatMoney(amount.toFixed(2))}`;
    }
  }

  setupHeatSettingOptionHandlers() {
    const heatSettingButton = document.querySelector('[data-option-group="heat-setting"]');
    if (!heatSettingButton) return;
    
    const heatSettingAccordion = heatSettingButton.closest('.product-accordion');
    if (!heatSettingAccordion) return;
    
    const heatSettingContainer = heatSettingAccordion.querySelector('.image-selector-container');
    const hiddenInput = heatSettingAccordion.querySelector('input[name="properties[Heat Setting]"]');
    const feeInput = heatSettingAccordion.querySelector('input[name="properties[_Heat Setting Fee]"]');
    const selectedOptionDisplay = heatSettingButton.querySelector('.selected-option-display');

    const container = document.querySelector('.custom-product-options-container');
    const productFormId = container?.dataset.productFormId || '';
    if (hiddenInput && productFormId) {
      hiddenInput.setAttribute('form', productFormId);
    }
    if (feeInput && productFormId) {
      feeInput.setAttribute('form', productFormId);
    }

    if (!heatSettingContainer) return;

    const options = heatSettingContainer.querySelectorAll('.image-option');
    options.forEach((option) => {
      option.addEventListener('click', () => {
        if (option.classList.contains('disabled')) {
          return;
        }
        
        options.forEach((opt) => opt.classList.remove('selected'));
        
        option.classList.add('selected');
        
        const heatSettingValue = option.dataset.value;
        const isHeatSetting = heatSettingValue === 'yes';
        
        if (hiddenInput) {
          hiddenInput.value = isHeatSetting ? 'Yes' : 'No';
        }
        
        if (feeInput) {
          const price = parseFloat(option.dataset.price) || 0;
          feeInput.value = price;
        }
        
        if (selectedOptionDisplay) {
          selectedOptionDisplay.textContent = isHeatSetting ? 'Heat Setting Process' : 'No Heat Setting';
        }
        
        const configChangeEvent = new CustomEvent('configurationChanged', {
          detail: { type: 'heatSetting', value: heatSettingValue }
        });
        document.dispatchEvent(configChangeEvent);
      });
    });

    // Set default selection to "No Heat Setting"
    const defaultOption = heatSettingContainer.querySelector('.image-option[data-value="no"]');
    if (defaultOption) {
      defaultOption.click();
    }
  }

  updateCompatibilityForFabric(fabricConfig) {
    if (!fabricConfig) return;

    const heatSettingAccordion = document.querySelector('[data-option-group="heat-setting"]')?.closest('.product-accordion');
    if (!heatSettingAccordion) return;

    const heatSettingOptions = heatSettingAccordion.querySelectorAll('.image-option');
    const compatibilityMessage = heatSettingAccordion.querySelector('.heat-setting-compatibility');
    const compatibilityInfo = compatibilityMessage?.querySelector('.compatibility-message');

    if (!fabricConfig.supports_heat_setting) {
      heatSettingOptions.forEach(option => {
        if (option.dataset.value === 'yes') {
          option.classList.add('disabled');
          option.style.opacity = '0.5';
          option.style.pointerEvents = 'none';
        }
      });

      if (compatibilityInfo) {
        compatibilityInfo.textContent = `⚠️ ${fabricConfig.name} does not support heat setting process.`;
        compatibilityMessage.style.display = 'block';
      }

      const noHeatOption = heatSettingAccordion.querySelector('.image-option[data-value="no"]');
      if (noHeatOption && !noHeatOption.classList.contains('selected')) {
        noHeatOption.click();
      }
    } else {
      heatSettingOptions.forEach(option => {
        option.classList.remove('disabled');
        option.style.opacity = '';
        option.style.pointerEvents = '';
      });

      if (compatibilityMessage) {
        compatibilityMessage.style.display = 'none';
      }
    }
    
    if (heatSettingAccordion && heatSettingAccordion.recalculateHeight) {
      requestAnimationFrame(() => {
        heatSettingAccordion.recalculateHeight();
      });
    }
  }

  showErrorState() {
    const heatSettingButton = document.querySelector('[data-option-group="heat-setting"]');
    if (!heatSettingButton) return;
    
    const heatSettingAccordion = heatSettingButton.closest('.product-accordion');
    if (!heatSettingAccordion) return;
    
    const loadingElement = heatSettingAccordion.querySelector('.heat-setting-loading');
    if (loadingElement) {
      loadingElement.innerHTML = '<p style="color: #d32f2f;">Error loading heat setting options. Please refresh the page.</p>';
    }
  }
}

// Dynamic Tie Backs Selector
class DynamicTieBacksSelector {
  constructor() {
    this.configLoader = null;
    this.isInitialized = false;
  }

  async initialize() {
    try {
      this.configLoader = await window.curtainConfigLoader.getInitializationPromise();
      
      this.generateTieBacksOptions();
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing dynamic tie backs selector:', error);
      this.showErrorState();
    }
  }

  generateTieBacksOptions() {
    const tieBacksButton = document.querySelector('[data-option-group="ties"]');
    if (!tieBacksButton) {
      console.error('Tie backs button not found');
      return;
    }
    
    const tieBacksAccordion = tieBacksButton.closest('.product-accordion');
    if (!tieBacksAccordion) {
      console.error('Tie backs accordion not found');
      return;
    }
    
    const tieBacksContainer = tieBacksAccordion.querySelector('.image-selector-container');
    const loadingElement = tieBacksAccordion.querySelector('.tie-backs-loading');
    
    if (!tieBacksContainer) {
      console.error('Tie backs container not found');
      return;
    }

    try {
      // Get tie backs options from config
      const additionalServices = this.configLoader.getAdditionalServices();
      const tieBacksConfig = additionalServices?.tie_backs_options || additionalServices?.ties;
      
      if (!tieBacksConfig) {
        console.error('Tie backs configuration not found');
        this.showErrorState();
        return;
      }

      // Generate tie backs options from config
      let tieBacksOptions = [];
      
      if (Array.isArray(tieBacksConfig)) {
        // If it's already an array of options
        tieBacksOptions = tieBacksConfig;
      } else {
        // Generate standard options using config data
        tieBacksOptions = [
          {
            id: 'no_tie_backs',
            name: 'No Tie Backs',
            value: 'none',
            image: tieBacksConfig.no_image || 'https://cdn.shopify.com/s/files/1/0569/8789/3266/files/no_tie_backs.jpg?v=1704067200',
            price: 0,
            enabled: true
          },
          {
            id: 'fabric_tie_backs',
            name: tieBacksConfig.name || 'Tie Backs',
            value: 'fabric_ties',
            image: tieBacksConfig.image || 'https://cdn.shopify.com/s/files/1/0569/8789/3266/files/tie_backs.jpg?v=1704067200',
            price: tieBacksConfig.price_per_piece || 10.00,
            enabled: tieBacksConfig.enabled !== false
          }
        ];
      }
      
      // Filter enabled options
      const enabledOptions = tieBacksOptions.filter(option => option.enabled !== false);
      
      enabledOptions.forEach(option => {
        const optionElement = this.createTieBacksOption(option);
        tieBacksContainer.appendChild(optionElement);
      });

      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
      tieBacksContainer.style.display = 'flex';

      this.setupTieBacksOptionHandlers();
      
      if (tieBacksAccordion && tieBacksAccordion.recalculateHeight) {
        tieBacksAccordion.recalculateHeight();
      }
      
    } catch (error) {
      console.error('Error generating tie backs options:', error);
      this.showErrorState();
    }
  }

  createTieBacksOption(option) {
    console.log('Creating tie backs option:', option);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'image-option';
    button.dataset.value = option.value;
    button.dataset.tieBacksId = option.id;
    button.dataset.price = option.price;

    button.innerHTML = `
      <img
        src="${option.image}"
        alt="${option.name}"
        width="100"
        height="100"
      >
      <div class="option-details">
        <div class="option-name">${option.name}</div>
        <div class="option-price">（+${option.price > 0 ? `${this.formatPrice(option.price * Shopify.currency.rate)} per curtain` : this.formatPrice(0)}）</div>
      </div>
    `;

    return button;
  }

  /**
   * Format price using calculator's currency formatting
   * @param {number} amount - Price amount
   * @returns {string} Formatted price string
   */
  formatPrice(amount) {
    try {
      // First try to use calculator's formatPrice method
      if (window.curtainProductOptions?.pricePreview?.calculator) {
        const calculator = window.curtainProductOptions.pricePreview.calculator;
        if (calculator.formatPrice) {
          return calculator.formatPrice(amount);
        }
      }

      // Fallback to PricePreviewManager's getFormattedPrice method
      if (window.curtainProductOptions?.pricePreview?.getFormattedPrice) {
        return window.curtainProductOptions.pricePreview.getFormattedPrice(amount);
      }

      // Final fallback
      return `$${amount.toFixed(2)}`;
    } catch (error) {
      console.warn('Error formatting price:', error);
      return `$${amount.toFixed(2)}`;
    }
  }

  setupTieBacksOptionHandlers() {
    const tieBacksButton = document.querySelector('[data-option-group="ties"]');
    if (!tieBacksButton) return;
    
    const tieBacksAccordion = tieBacksButton.closest('.product-accordion');
    if (!tieBacksAccordion) return;
    
    const tieBacksContainer = tieBacksAccordion.querySelector('.image-selector-container');
    const hiddenInputs = {
      tieType: tieBacksAccordion.querySelector('input[name="properties[Tie Backs]"]'),
      quantity: tieBacksAccordion.querySelector('input[name="properties[_Tie Backs Quantity]"]'),
      fee: tieBacksAccordion.querySelector('input[name="properties[_Tie Backs Fee]"]')
    };
    const selectedOptionDisplay = tieBacksButton.querySelector('.selected-option-display');

    const container = document.querySelector('.custom-product-options-container');
    const productFormId = container?.dataset.productFormId || '';
    Object.values(hiddenInputs).forEach(input => {
      if (input && productFormId) {
        input.setAttribute('form', productFormId);
      }
    });

    if (!tieBacksContainer) return;

    const options = tieBacksContainer.querySelectorAll('.image-option');
    options.forEach((option) => {
      option.addEventListener('click', () => {
        options.forEach((opt) => opt.classList.remove('selected'));
        
        option.classList.add('selected');
        
        this.updateCalculation(option);
      });
    });

    // Set default selection to "No Tie Backs"
    const defaultOption = tieBacksContainer.querySelector('.image-option[data-value="none"]');
    if (defaultOption) {
      defaultOption.click();
    }
  }

  updateCalculation(selectedOption) {
    const tieBacksButton = document.querySelector('[data-option-group="ties"]');
    if (!tieBacksButton) return;
    
    const tieBacksAccordion = tieBacksButton.closest('.product-accordion');
    if (!tieBacksAccordion) return;

    const hiddenInputs = {
      tieType: tieBacksAccordion.querySelector('input[name="properties[Tie Backs]"]'),
      quantity: tieBacksAccordion.querySelector('input[name="properties[_Tie Backs Quantity]"]'),
      fee: tieBacksAccordion.querySelector('input[name="properties[_Tie Backs Fee]"]')
    };
    const selectedOptionDisplay = tieBacksButton.querySelector('.selected-option-display');

    const getCurtainQuantity = () => {
      const container = document.querySelector('.custom-product-options-container');
      const productFormId = container?.dataset.productFormId;
      if (productFormId) {
        const productForm = document.getElementById(productFormId);
        if (productForm) {
          const quantityInput = productForm.querySelector('input[name="quantity"]');
          return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        }
      }
      return 1;
    };

    const curtainQuantity = getCurtainQuantity();
    const tieValue = selectedOption.dataset.value;
    const pricePerCurtain = parseFloat(selectedOption.dataset.price) || 0;
    
    let totalQuantity = 0;
    let totalFee = 0;
    let displayText = '';

    if (tieValue === 'none') {
      displayText = 'No tie backs';
    } else if (tieValue === 'fabric_ties') {
      totalQuantity = curtainQuantity;
      totalFee = curtainQuantity * pricePerCurtain;
      displayText = `${curtainQuantity} tie backs (1 per curtain)`;
    }

    if (hiddenInputs.tieType) hiddenInputs.tieType.value = tieValue;
    if (hiddenInputs.quantity) hiddenInputs.quantity.value = totalQuantity;
    if (hiddenInputs.fee) hiddenInputs.fee.value = totalFee;

    if (selectedOptionDisplay) {
      selectedOptionDisplay.textContent = displayText;
    }

    const configChangeEvent = new CustomEvent('configurationChanged', {
      detail: { type: 'tiebacks', value: selectedOption.dataset.value }
    });
    document.dispatchEvent(configChangeEvent);
  }

  showErrorState() {
    const tieBacksButton = document.querySelector('[data-option-group="ties"]');
    if (!tieBacksButton) return;
    
    const tieBacksAccordion = tieBacksButton.closest('.product-accordion');
    if (!tieBacksAccordion) return;
    
    const loadingElement = tieBacksAccordion.querySelector('.tie-backs-loading');
    if (loadingElement) {
      loadingElement.innerHTML = '<p style="color: #d32f2f;">Error loading tie backs options. Please refresh the page.</p>';
    }
  }
}